import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AppTab, DealershipTask, Employee, UserRole, TaskParticipant, TaskStatus, TaskType, RecurrenceFrequency, TaskPriority, TaskRemark, StaffManagerLink, TaskProof } from './types';
import Dashboard from './components/Dashboard';
import AppHeader from './components/AppHeader';
import DevAdminPanel from './components/DevAdminPanel';
import StatsScreen from './components/StatsScreen';
import TeamManager from './components/TeamManager';
import ApprovalsPanel from './components/ApprovalsPanel';
import LoginScreen from './components/LoginScreen';
import LeadsScreen from './src/screens/CRM/LeadsScreen';
import ErrorBoundary from './src/components/ErrorBoundary';
import { supabase } from './src/lib/supabase';
import { useNotificationDeepLink } from './src/hooks/useNotificationDeepLink';
import { useNotificationSetup } from './src/hooks/useNotificationSetup';
import { transformTaskToApp, transformTaskToDB, transformTasksToApp, DatabaseTask } from './src/utils/transformers';
import { getLatestTaskProofTimestamp } from './src/utils/taskProofs';
import { resolveCurrentEmployeeIdForApproval, resolveEmployeeIdForApproval } from './src/utils/approvalEmployeeIds';
import { getCurrentCompanyId, getCurrentUserId, logSupabaseQueryError, setErrorLoggingContext } from './src/utils/errorLogging';
import { sendTaskAssignmentNotification, sendTaskCollaborationNotification, sendTaskCompletionNotification } from './src/utils/pushNotifications';
import { useAuthCompany } from './src/context/AuthCompanyContext';
import { Toaster, toast } from 'sonner';
import { Analytics } from '@vercel/analytics/react';
import OneSignal from 'react-onesignal';
import {
  ClipboardList,
  CheckCircle2,
  Users,
  LayoutDashboard,
  Loader2,
  AlertTriangle,
  Bell,
  X,
  BriefcaseBusiness
} from 'lucide-react';

const DEFAULT_COMPANY_ID = '00000000-0000-0000-0000-000000000001';
const DEV_ADMIN_EMAIL = 'sanilpetkar99@gmail.com';
const SUPER_ADMIN_EMAIL = DEV_ADMIN_EMAIL;
const DEV_ADMIN_PATH = '/dev-admin';
const USER_CACHE_KEY = 'universal_app_user';
const ACTIVE_TAB_CACHE_KEY = 'universal_app_active_tab';
const ACTIVE_COMPANY_CACHE_KEY = 'universal_app_active_company';
const EMPLOYEES_CACHE_KEY = 'universalAppEmployees';
const TASKS_CACHE_KEY = 'universalAppTasks';
const STAFF_MANAGER_LINKS_CACHE_KEY = 'universalAppStaffManagerLinks';
const TASK_PARTICIPANTS_CACHE_KEY = 'universalAppTaskParticipants';
const HIGH_PRIORITY_COMPLETION_PREFIX = 'High Priority Task Completion:';
const EXTENSION_REQUEST_PREFIX = 'Deadline Extension Request:';
const DAILY_MS = 24 * 60 * 60 * 1000;
const WEEKLY_MS = 7 * DAILY_MS;
const MONTHLY_MS = 30 * DAILY_MS;
const getRecurrenceIntervalMs = (frequency: RecurrenceFrequency | null | undefined): number => {
  if (frequency === 'daily') return DAILY_MS;
  if (frequency === 'weekly') return WEEKLY_MS;
  if (frequency === 'monthly') return MONTHLY_MS;
  return 0;
};

const computeNextRecurrenceNotificationAt = (
  baseTimestamp: number,
  frequency: RecurrenceFrequency | null | undefined
): number | null => {
  const intervalMs = getRecurrenceIntervalMs(frequency);
  if (!intervalMs) return null;
  return Number(baseTimestamp || Date.now()) + intervalMs;
};

const calculateFirstResurfaceAt = (recurrenceTime: string): number | null => {
  const match = /^(\d{2}):(\d{2})$/.exec(String(recurrenceTime || '').trim());
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  const resurface = new Date();
  resurface.setUTCDate(resurface.getUTCDate() + 1);
  // recurrence_time is stored in IST; convert it to UTC for the persisted bigint timestamp.
  resurface.setUTCHours(hours - 5, minutes - 30, 0, 0);
  return resurface.getTime();
};

const resolveRecurringFrequencyForTask = (
  task: DealershipTask | undefined
): RecurrenceFrequency | null => {
  if (!task) return null;

  const rawTaskType = String((task as any).taskType ?? (task as any).task_type ?? 'one_time').toLowerCase();
  if (rawTaskType !== 'recurring') {
    return null;
  }

  const rawFrequency = String(
    (task as any).recurrenceFrequency ?? (task as any).recurrence_frequency ?? ''
  ).toLowerCase();

  if (rawFrequency === 'daily' || rawFrequency === 'weekly' || rawFrequency === 'monthly') {
    return rawFrequency as RecurrenceFrequency;
  }

  return null;
};

const normalizeTaskPriority = (task: DealershipTask | undefined): TaskPriority => {
  const rawPriority = String((task as any)?.priority || '').trim().toLowerCase();
  return rawPriority === 'high' ? 'High' : rawPriority === 'low' ? 'Low' : 'Medium';
};

const normalizeRole = (role: unknown, email?: unknown): Employee['role'] => {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  if (normalizedEmail === SUPER_ADMIN_EMAIL) {
    return 'super_admin';
  }

  return role === 'owner' || role === 'manager' || role === 'staff' || role === 'super_admin' || role === 'accountant'
    ? role
    : 'staff';
};

const getRoleLabel = (role: Employee['role']): string => {
  if (role === 'super_admin' || role === 'owner') return 'Owner';
  if (role === 'manager') return 'Manager';
  if (role === 'accountant') return 'Accountant';
  return 'Staff';
};

const isManagerLevelRole = (role: unknown): boolean =>
  role === 'manager' || role === 'owner' || role === 'super_admin';

const canAccessTeamTab = (role: Employee['role'] | null | undefined): boolean => {
  return role === 'manager' || role === 'super_admin' || role === 'owner';
};

const normalizePersistedAppTab = (tabValue: string | null): AppTab | null => {
  if (tabValue === AppTab.TASKS || tabValue === AppTab.APPROVALS || tabValue === AppTab.TEAM || tabValue === AppTab.CRM) {
    return tabValue;
  }

  return null;
};

const resolveActiveTabForRole = (
  nextTab: AppTab | null | undefined,
  role: Employee['role'] | null | undefined
): AppTab => {
  if (nextTab === AppTab.APPROVALS) return AppTab.APPROVALS;
  if (nextTab === AppTab.CRM) return AppTab.CRM;
  if (nextTab === AppTab.TEAM && canAccessTeamTab(role)) return AppTab.TEAM;
  return AppTab.TASKS;
};

const readPersistedActiveTab = (userId?: string | null): AppTab | null => {
  if (typeof window === 'undefined') return null;

  const normalizedUserId = String(userId || '').trim();
  const lookupKeys = normalizedUserId
    ? [`${ACTIVE_TAB_CACHE_KEY}:${normalizedUserId}`, ACTIVE_TAB_CACHE_KEY]
    : [ACTIVE_TAB_CACHE_KEY];

  for (const key of lookupKeys) {
    try {
      const rawTab = localStorage.getItem(key);
      const parsedTab = normalizePersistedAppTab(rawTab);
      if (parsedTab) return parsedTab;
    } catch {
      // Ignore local storage read failures and continue with defaults.
    }
  }

  return null;
};

const toFallbackEmployeeFromAuthUser = (authUser: any): Employee => {
  const metadata = authUser?.user_metadata || {};
  const email = String(authUser?.email || metadata.email || '').trim();
  const name = String(metadata.name || email.split('@')[0] || 'User').trim();
  const mobile = String(metadata.mobile || '').trim();

  return {
    id: String(authUser?.id || `temp-${Date.now()}`),
    name: name || 'User',
    email: email || `${authUser?.id || 'user'}@taskpro.local`,
    mobile: mobile || String(authUser?.id || '0000000000').slice(0, 10),
    role: normalizeRole(metadata.role, email),
    company_id: String(metadata.company_id || DEFAULT_COMPANY_ID),
  };
};

const normalizeMobile = (mobile: unknown): string => {
  return String(mobile || '').replace(/\D/g, '');
};

const isMissingColumnError = (error: any): boolean => {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('column') && message.includes('does not exist');
};

const isMissingRelationError = (error: any): boolean => {
  const message = String(error?.message || '').toLowerCase();
  const code = String(error?.code || '').toUpperCase();
  return code === '42P01' || (message.includes('relation') && message.includes('does not exist'));
};

const isMissingTaskRecurrenceColumnError = (error: any): boolean => {
  const message = String(error?.message || '').toLowerCase();
  const missingRecurrenceColumn =
    message.includes('recurrence_frequency') || message.includes('task_type') || message.includes('recurrence_time');
  const missingInSchema =
    message.includes('schema cache') || (message.includes('column') && message.includes('does not exist'));
  return missingRecurrenceColumn && missingInSchema;
};

const isMissingNextRecurrenceNotificationColumnError = (error: any): boolean => {
  const message = String(error?.message || '').toLowerCase();
  return (
    message.includes('next_recurrence_notification_at') &&
    (message.includes('schema cache') || (message.includes('column') && message.includes('does not exist')))
  );
};

const stripTaskRecurrenceFields = (payload: Record<string, any>) => {
  const legacyPayload = { ...payload };
  delete legacyPayload.task_type;
  delete legacyPayload.recurrence_frequency;
  delete legacyPayload.recurrence_time;
  delete legacyPayload.taskType;
  delete legacyPayload.recurrenceFrequency;
  delete legacyPayload.recurrenceTime;
  delete legacyPayload.next_recurrence_notification_at;
  delete legacyPayload.nextRecurrenceNotificationAt;
  return legacyPayload;
};

const toCamelTaskRecurrencePayload = (payload: Record<string, any>) => {
  const camelPayload = { ...payload };
  camelPayload.taskType = camelPayload.task_type ?? camelPayload.taskType ?? 'one_time';
  camelPayload.recurrenceFrequency =
    camelPayload.recurrence_frequency ?? camelPayload.recurrenceFrequency ?? null;
  camelPayload.recurrenceTime =
    camelPayload.recurrence_time ?? camelPayload.recurrenceTime ?? null;
  camelPayload.nextRecurrenceNotificationAt =
    camelPayload.next_recurrence_notification_at ?? camelPayload.nextRecurrenceNotificationAt ?? null;
  delete camelPayload.task_type;
  delete camelPayload.recurrence_frequency;
  delete camelPayload.recurrence_time;
  delete camelPayload.next_recurrence_notification_at;
  return camelPayload;
};

const stripTaskNextRecurrenceField = (payload: Record<string, any>) => {
  const legacyPayload = { ...payload };
  delete legacyPayload.next_recurrence_notification_at;
  delete legacyPayload.nextRecurrenceNotificationAt;
  return legacyPayload;
};

const isEmployeesPolicyError = (error: any): boolean => {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('infinite recursion') && message.includes('employees');
};

const isPolicyRecursionError = (error: any): boolean => {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('infinite recursion');
};

const parseCachedArray = <T,>(key: string): T[] => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw || raw === 'undefined') return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed as T[] : [];
  } catch {
    return [];
  }
};

const filterEmployeesByCompany = (employeeRows: Employee[], companyId: string | null): Employee[] => {
  if (!companyId) {
    return employeeRows;
  }
  return employeeRows.filter((employee) => String(employee?.company_id || '').trim() === companyId);
};

const isCompletedStatus = (status: unknown): boolean =>
  String(status || '').toLowerCase() === 'completed';

const filterActiveTasks = (taskRows: DealershipTask[]): DealershipTask[] =>
  taskRows.filter((task) => !isCompletedStatus(task.status));

const filterTasksByCompany = (taskRows: DealershipTask[], companyId: string | null): DealershipTask[] => {
  if (!companyId) {
    return taskRows;
  }
  return taskRows.filter((task) => {
    const taskCompanyId = String(task?.company_id || '').trim();
    // Keep legacy cached tasks that were missing company_id due older transforms.
    return !taskCompanyId || taskCompanyId === companyId;
  });
};

const normalizeEmployeeProfile = (employee: Partial<Employee> & { id: string }): Employee => {
  const safeId = String(employee.id || `temp-${Date.now()}`);
  const safeEmail = String(employee.email || `${safeId}@taskpro.local`).trim();
  const safeName = String(employee.name || safeEmail.split('@')[0] || 'User').trim();
  const safeMobile = String(employee.mobile || '').trim();
  const rawManagerId = (employee as any)?.manager_id ?? (employee as any)?.managerId;
  const safeManagerId =
    typeof rawManagerId === 'string' && rawManagerId.trim()
      ? rawManagerId.trim()
      : null;

  return {
    id: safeId,
    name: safeName || 'User',
    email: safeEmail,
    mobile: safeMobile || safeId.slice(0, 10),
    role: normalizeRole(employee.role, safeEmail),
    company_id: String(employee.company_id || DEFAULT_COMPANY_ID),
    onesignal_id: employee.onesignal_id ? String(employee.onesignal_id) : null,
    auth_user_id: employee.auth_user_id ? String(employee.auth_user_id) : undefined,
    manager_id: safeManagerId,
  };
};

const filterStaffManagerLinksByCompany = (
  links: StaffManagerLink[],
  companyId: string | null
): StaffManagerLink[] => {
  if (!companyId) {
    return links;
  }
  return links.filter((link) => String(link?.company_id || '').trim() === companyId);
};

const normalizeTaskParticipant = (
  participant: Partial<TaskParticipant> & { task_id?: string | null; user_id?: string | null }
): TaskParticipant | null => {
  const taskId = String(participant?.task_id || '').trim();
  const userId = String(participant?.user_id || '').trim();
  const addedBy = String(participant?.added_by || '').trim();

  if (!taskId || !userId) {
    return null;
  }

  return {
    task_id: taskId,
    user_id: userId,
    added_by: addedBy,
    added_at: typeof participant?.added_at === 'string' ? participant.added_at : undefined,
    role: participant?.role ? String(participant.role) : null,
    company_id: participant?.company_id ? String(participant.company_id) : undefined,
  };
};

const filterTaskParticipantsByCompany = (
  participants: TaskParticipant[],
  companyId: string | null
): TaskParticipant[] => {
  const normalizedParticipants = (participants || [])
    .map((participant) => normalizeTaskParticipant(participant))
    .filter((participant): participant is TaskParticipant => Boolean(participant));

  if (!companyId) {
    return normalizedParticipants;
  }

  return normalizedParticipants.filter((participant) => {
    const participantCompanyId = String(participant.company_id || '').trim();
    return !participantCompanyId || participantCompanyId === companyId;
  });
};

const getTaskParticipantUserIds = (
  task: Partial<DealershipTask> | Record<string, unknown> | null | undefined
): string[] => {
  if (!task) {
    return [];
  }

  const directIds = Array.isArray((task as any).participantUserIds)
    ? (task as any).participantUserIds
        .map((value: unknown) => String(value || '').trim())
        .filter(Boolean)
    : [];

  const participantIds = Array.isArray((task as any).participants)
    ? (task as any).participants
        .map((participant: Partial<TaskParticipant>) => String(participant?.user_id || '').trim())
        .filter(Boolean)
    : [];

  return Array.from(new Set([...directIds, ...participantIds]));
};

const attachTaskParticipantsToTasks = (
  taskRows: DealershipTask[],
  participantRows: TaskParticipant[]
): DealershipTask[] => {
  const participantsByTaskId = new Map<string, TaskParticipant[]>();

  (participantRows || []).forEach((participant) => {
    const normalized = normalizeTaskParticipant(participant);
    if (!normalized) {
      return;
    }

    const existing = participantsByTaskId.get(normalized.task_id) || [];
    existing.push(normalized);
    participantsByTaskId.set(normalized.task_id, existing);
  });

  return (taskRows || []).map((task) => {
    const participants = participantsByTaskId.get(String(task.id || '').trim()) || [];
    return {
      ...task,
      participants,
      participantUserIds: Array.from(
        new Set(participants.map((participant) => String(participant.user_id || '').trim()).filter(Boolean))
      ),
    };
  });
};

const getParticipantTaskIdsForScope = (
  participantRows: TaskParticipant[],
  scopeIds: string[] = []
): string[] => {
  if (!scopeIds.length) {
    return [];
  }

  const scopeSet = new Set(scopeIds.map((id) => String(id || '').trim()).filter(Boolean));
  const taskIds = new Set<string>();

  (participantRows || []).forEach((participant) => {
    const normalized = normalizeTaskParticipant(participant);
    if (!normalized) {
      return;
    }

    if (scopeSet.has(normalized.user_id)) {
      taskIds.add(normalized.task_id);
    }
  });

  return Array.from(taskIds);
};

const getManagerIdsForStaff = (
  staffId: string,
  staffManagerLinks: StaffManagerLink[],
  fallbackManagerId?: string | null
): string[] => {
  const normalizedStaffId = String(staffId || '').trim();
  if (!normalizedStaffId) {
    return [];
  }

  const ids = new Set<string>();
  for (const link of staffManagerLinks || []) {
    if (!link) continue;
    if (String(link.staff_id || '').trim() !== normalizedStaffId) continue;
    const managerId = String(link.manager_id || '').trim();
    if (managerId) {
      ids.add(managerId);
    }
  }

  const fallback = String(fallbackManagerId || '').trim();
  if (fallback) {
    ids.add(fallback);
  }

  return Array.from(ids);
};

const resolveTaskCompletionApproverId = (
  task: DealershipTask,
  currentUser: Employee,
  employees: Employee[],
  staffManagerLinks: StaffManagerLink[]
): string | null => {
  const candidateIds: string[] = [];
  const pushCandidate = (candidateId?: string | null) => {
    const normalized = String(candidateId || '').trim();
    if (!normalized || normalized === currentUser.id || candidateIds.includes(normalized)) {
      return;
    }
    candidateIds.push(normalized);
  };

  const assigner = employees.find((employee) => employee.id === task.assignedBy);
  if (assigner && isManagerLevelRole(assigner.role)) {
    pushCandidate(assigner.id);
  }

  getManagerIdsForStaff(currentUser.id, staffManagerLinks, currentUser.manager_id || null).forEach(pushCandidate);

  employees
    .filter((employee) => isManagerLevelRole(employee.role))
    .sort((left, right) => left.name.localeCompare(right.name))
    .forEach((employee) => pushCandidate(employee.id));

  return candidateIds[0] || null;
};

const createTaskRemarkEntry = (
  taskId: string,
  employee: Pick<Employee, 'id' | 'name'>,
  remark: string,
  timestamp: number = Date.now()
): TaskRemark => ({
  id: `remark_${taskId}_${timestamp}`,
  taskId,
  employeeId: employee.id,
  employeeName: employee.name,
  remark,
  timestamp,
});

const scopeEmployeesForCurrentUser = (
  employeeRows: Employee[],
  taskRows: DealershipTask[],
  currentUser: Employee | null,
  staffManagerLinks: StaffManagerLink[] = []
): Employee[] => {
  const mergedEmployees = mergeCurrentUserIntoEmployees(employeeRows, currentUser);
  if (!currentUser) {
    return mergedEmployees;
  }

  const taskRelatedEmployeeIds = new Set<string>();
  for (const task of taskRows || []) {
    const assignedTo = String(task?.assignedTo || '').trim();
    const assignedBy = String(task?.assignedBy || '').trim();
    if (assignedTo) {
      taskRelatedEmployeeIds.add(assignedTo);
    }
    if (assignedBy) {
      taskRelatedEmployeeIds.add(assignedBy);
    }
    getTaskParticipantUserIds(task).forEach((participantId) => taskRelatedEmployeeIds.add(participantId));
  }

  if (currentUser.role === 'super_admin' || currentUser.role === 'owner' || currentUser.role === 'accountant') {
    return mergedEmployees;
  }

  if (currentUser.role === 'manager') {
    const visibleAssignerIds = new Set<string>([currentUser.id]);
    for (const task of taskRows || []) {
      if (task.assignedTo === currentUser.id && task.assignedBy) {
        visibleAssignerIds.add(task.assignedBy);
      }
    }

    return mergedEmployees.filter((employee) => {
      if (taskRelatedEmployeeIds.has(employee.id)) return true;
      if (visibleAssignerIds.has(employee.id)) return true;
      if (employee.id === currentUser.id) return true;
      if (employee.role !== 'staff') return false;
      const managerIds = getManagerIdsForStaff(
        employee.id,
        staffManagerLinks,
        typeof employee.manager_id === 'string' ? employee.manager_id : null
      );
      return managerIds.includes(currentUser.id);
    });
  }

  // Staff: show own profile and direct manager when available.
  const managerIds = new Set<string>();
  const profileManagerIds = getManagerIdsForStaff(
    currentUser.id,
    staffManagerLinks,
    typeof currentUser.manager_id === 'string' ? currentUser.manager_id : null
  );
  for (const managerId of profileManagerIds) {
    managerIds.add(managerId);
  }

  taskRows.forEach((task) => {
    if (task.assignedTo === currentUser.id && task.assignedBy) {
      managerIds.add(task.assignedBy);
    }
  });

  return mergedEmployees.filter(
    (employee) =>
      employee.id === currentUser.id ||
      managerIds.has(employee.id) ||
      taskRelatedEmployeeIds.has(employee.id)
  );
};

const mergeCurrentUserIntoEmployees = (employeeRows: Employee[], currentUser: Employee | null): Employee[] => {
  const normalizedRows = (employeeRows || []).map((employee) => normalizeEmployeeProfile(employee));
  if (!currentUser) {
    return normalizedRows;
  }

  const normalizedCurrentUser = normalizeEmployeeProfile(currentUser);
  const existingIndex = normalizedRows.findIndex((employee) => employee.id === normalizedCurrentUser.id);

  if (existingIndex >= 0) {
    const mergedRows = [...normalizedRows];
    mergedRows[existingIndex] = { ...mergedRows[existingIndex], ...normalizedCurrentUser };
    return mergedRows;
  }

  return [normalizedCurrentUser, ...normalizedRows];
};

const syncEmployeeProfileToDatabase = async (employee: Employee, authUserId?: string): Promise<Employee | null> => {
  const normalizedProfile = normalizeEmployeeProfile(employee);
  const payloadWithAuthLink = {
    id: normalizedProfile.id,
    name: normalizedProfile.name,
    email: normalizedProfile.email,
    mobile: normalizedProfile.mobile,
    role: normalizedProfile.role,
    company_id: normalizedProfile.company_id,
    auth_user_id: authUserId || normalizedProfile.auth_user_id || null,
    updated_at: new Date().toISOString(),
  };

  let { data, error } = await supabase
    .from('employees')
    .upsert(payloadWithAuthLink, { onConflict: 'id' })
    .select('*')
    .maybeSingle();

  if (error && isMissingColumnError(error)) {
    const { data: retryData, error: retryError } = await supabase
      .from('employees')
      .upsert({
        id: normalizedProfile.id,
        name: normalizedProfile.name,
        email: normalizedProfile.email,
        mobile: normalizedProfile.mobile,
        role: normalizedProfile.role,
        company_id: normalizedProfile.company_id,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'id' })
      .select('*')
      .maybeSingle();

    data = retryData;
    error = retryError;
  }

  if (error) {
    if (!isEmployeesPolicyError(error)) {
      console.warn('Employee profile auto-sync failed:', error);
      logSupabaseQueryError(error);
    }
    return null;
  }

  return data ? normalizeEmployeeProfile(data as Employee) : null;
};

const resolveEmployeeProfileFromAuthUser = async (authUser: any): Promise<Employee | null> => {
  const authUserId = String(authUser?.id || '').trim();
  const authEmail = String(authUser?.email || '').trim().toLowerCase();
  const authMobile = normalizeMobile(authUser?.user_metadata?.mobile || authUser?.phone || '');
  const authCompanyId = String(authUser?.user_metadata?.company_id || '').trim();

  if (authUserId) {
    const { data: byId, error: byIdError } = await supabase
      .from('employees')
      .select('*')
      .eq('id', authUserId)
      .limit(1)
      .maybeSingle();

    if (byId) {
      return byId as Employee;
    }

    if (byIdError && !isEmployeesPolicyError(byIdError)) {
      console.warn('Session profile lookup by id failed:', byIdError);
      logSupabaseQueryError(byIdError);
    }

    let byAuthUserIdQuery = supabase
      .from('employees')
      .select('*')
      .eq('auth_user_id', authUserId)
      .limit(1);

    if (authCompanyId) {
      byAuthUserIdQuery = byAuthUserIdQuery.eq('company_id', authCompanyId);
    }

    const { data: byAuthUserId, error: byAuthUserIdError } = await byAuthUserIdQuery.maybeSingle();

    if (byAuthUserId) {
      return byAuthUserId as Employee;
    }

    if (byAuthUserIdError && !isMissingColumnError(byAuthUserIdError) && !isEmployeesPolicyError(byAuthUserIdError)) {
      console.warn('Session profile lookup by auth_user_id failed:', byAuthUserIdError);
      logSupabaseQueryError(byAuthUserIdError);
    }
  }

  if (authEmail) {
    let byEmailQuery = supabase
      .from('employees')
      .select('*')
      .eq('email', authEmail)
      .limit(1);

    if (authCompanyId) {
      byEmailQuery = byEmailQuery.eq('company_id', authCompanyId);
    }

    const { data: byEmail, error: byEmailError } = await byEmailQuery.maybeSingle();

    if (byEmail) {
      return byEmail as Employee;
    }

    if (byEmailError && !isMissingColumnError(byEmailError) && !isEmployeesPolicyError(byEmailError)) {
      console.warn('Session profile lookup by email failed:', byEmailError);
      logSupabaseQueryError(byEmailError);
    }
  }

  if (authMobile) {
    let mobileQuery = supabase
      .from('employees')
      .select('*')
      .limit(200);

    if (authCompanyId) {
      mobileQuery = mobileQuery.eq('company_id', authCompanyId);
    }

    const { data: employeeRows, error: mobileError } = await mobileQuery;

    if (employeeRows && employeeRows.length > 0) {
      const matchedByMobile = employeeRows.find((emp: any) => normalizeMobile(emp?.mobile) === authMobile);
      if (matchedByMobile) {
        return matchedByMobile as Employee;
      }
    }

    if (mobileError && !isEmployeesPolicyError(mobileError)) {
      console.warn('Session profile lookup by mobile failed:', mobileError);
      logSupabaseQueryError(mobileError);
    }
  }

  return null;
};

const upsertTaskAtTop = <T extends { id: string }>(items: T[], nextItem: T): T[] => {
  const filtered = items.filter((item) => item.id !== nextItem.id);
  return [nextItem, ...filtered];
};

const upsertTaskInPlace = <T extends { id: string }>(items: T[], nextItem: T): T[] => {
  const index = items.findIndex((item) => item.id === nextItem.id);
  if (index === -1) {
    return [nextItem, ...items];
  }

  const updated = [...items];
  updated[index] = { ...updated[index], ...nextItem };
  return updated;
};

const upsertTaskParticipantRows = (rows: TaskParticipant[], nextRow: TaskParticipant): TaskParticipant[] => {
  const normalized = normalizeTaskParticipant(nextRow);
  if (!normalized) {
    return rows;
  }

  const nextKey = `${normalized.task_id}:${normalized.user_id}`;
  const remaining = (rows || []).filter((row) => {
    const current = normalizeTaskParticipant(row);
    return current ? `${current.task_id}:${current.user_id}` !== nextKey : true;
  });

  return [...remaining, normalized];
};

const buildTaskParticipantRows = (
  taskId: string,
  participantIds: string[],
  addedBy: string,
  companyId: string,
  role: string = 'delegated'
): TaskParticipant[] => {
  const normalizedTaskId = String(taskId || '').trim();
  const normalizedAddedBy = String(addedBy || '').trim();
  const normalizedCompanyId = String(companyId || '').trim();

  if (!normalizedTaskId || !normalizedAddedBy || !normalizedCompanyId) {
    return [];
  }

  return Array.from(new Set((participantIds || []).map((id) => String(id || '').trim()).filter(Boolean))).map((userId) => ({
    task_id: normalizedTaskId,
    user_id: userId,
    added_by: normalizedAddedBy,
    company_id: normalizedCompanyId,
    role,
  }));
};

const buildTaskScopeIds = (currentUser: Employee | null, employeeRows: Employee[] = []): string[] => {
  if (!currentUser) {
    return [];
  }

  const ids = new Set<string>();
  const normalizedEmail = String(currentUser.email || '').trim().toLowerCase();
  const normalizedMobile = normalizeMobile(currentUser.mobile || '');
  const currentAuthUserId = String((currentUser as any).auth_user_id || '').trim();

  const addId = (value: unknown) => {
    const id = String(value || '').trim();
    if (id) ids.add(id);
  };

  addId(currentUser.id);
  addId(currentAuthUserId);

  for (const employee of employeeRows || []) {
    if (!employee) continue;

    const employeeEmail = String(employee.email || '').trim().toLowerCase();
    const employeeMobile = normalizeMobile(employee.mobile || '');
    const employeeAuthUserId = String((employee as any).auth_user_id || '').trim();

    const matchByAuth = currentAuthUserId && employeeAuthUserId && employeeAuthUserId === currentAuthUserId;
    const matchByEmail = normalizedEmail && employeeEmail && employeeEmail === normalizedEmail;
    const matchByMobile = normalizedMobile && employeeMobile && employeeMobile === normalizedMobile;

    if (matchByAuth || matchByEmail || matchByMobile) {
      addId(employee.id);
      addId(employeeAuthUserId);
    }
  }

  return Array.from(ids);
};

const applyTaskVisibilityFilter = (
  query: any,
  currentUser: Employee | null,
  employeeRows: Employee[] = [],
  participantTaskIds: string[] = []
) => {
  if (!currentUser || currentUser.role === 'super_admin' || currentUser.role === 'owner') {
    return query;
  }

  const scopeIds = buildTaskScopeIds(currentUser, employeeRows);
  const clauses: string[] = [];

  if (scopeIds.length === 1) {
    const id = scopeIds[0];
    clauses.push(`assignedTo.eq.${id}`, `assignedBy.eq.${id}`);
  } else if (scopeIds.length > 1) {
    const scopedIdCsv = scopeIds.join(',');
    clauses.push(`assignedTo.in.(${scopedIdCsv})`, `assignedBy.in.(${scopedIdCsv})`);
  }

  if (participantTaskIds.length === 1) {
    clauses.push(`id.eq.${participantTaskIds[0]}`);
  } else if (participantTaskIds.length > 1) {
    clauses.push(`id.in.(${participantTaskIds.join(',')})`);
  }

  if (clauses.length === 0) {
    return query;
  }

  return query.or(clauses.join(','));
};

const isTaskVisibleToUser = (
  taskRow: Partial<DatabaseTask> | Record<string, unknown>,
  currentUser: Employee | null,
  scopeIds: string[] = [],
  participantTaskIds: string[] = []
): boolean => {
  if (!currentUser) return false;
  if (currentUser.role === 'super_admin' || currentUser.role === 'owner') return true;

  const row = taskRow as Record<string, unknown>;
  const taskId = String(row.id || '').trim();
  const assignedTo = String(row.assignedTo ?? row.assigned_to ?? '').trim();
  const assignedBy = String(row.assignedBy ?? row.assigned_by ?? '').trim();
  const candidateIds = scopeIds.length > 0 ? scopeIds : buildTaskScopeIds(currentUser);
  const isParticipantTask = participantTaskIds.some((id) => id === taskId);
  return isParticipantTask || candidateIds.some((id) => id === assignedTo || id === assignedBy);
};

const isHiddenSelfAssignedTaskForSuperAdmin = (
  taskRow: Partial<DatabaseTask> | DealershipTask | Record<string, unknown>,
  currentUser: Employee | null
): boolean => {
  if (currentUser?.role !== 'super_admin') {
    return false;
  }

  const row = taskRow as Record<string, unknown>;
  const assignedTo = String(row.assignedTo ?? row.assigned_to ?? '').trim();
  const assignedBy = String(row.assignedBy ?? row.assigned_by ?? '').trim();
  const isSelfAssigned = Boolean(assignedTo) && assignedTo === assignedBy;
  const isMyOwnTask = assignedTo === currentUser.id || assignedBy === currentUser.id;

  return isSelfAssigned && !isMyOwnTask;
};

const filterSuperAdminVisibleTasks = <T extends Partial<DatabaseTask> | DealershipTask | Record<string, unknown>>(
  taskRows: T[],
  currentUser: Employee | null
): T[] => taskRows.filter((taskRow) => !isHiddenSelfAssignedTaskForSuperAdmin(taskRow, currentUser));

const toTimestampNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const numericCandidate = Number(value);
    if (Number.isFinite(numericCandidate)) {
      return numericCandidate;
    }

    const parsedDate = Date.parse(value);
    if (Number.isFinite(parsedDate)) {
      return parsedDate;
    }
  }

  return Date.now();
};

const toOptionalTimestampNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const numericCandidate = Number(value);
    if (Number.isFinite(numericCandidate) && numericCandidate > 0) {
      return numericCandidate;
    }

    const parsedDate = Date.parse(value);
    if (Number.isFinite(parsedDate) && parsedDate > 0) {
      return parsedDate;
    }
  }

  return null;
};

const normalizeRealtimeRemarks = (rawRemarks: unknown, taskId: string): TaskRemark[] | null => {
  if (!Array.isArray(rawRemarks)) {
    return null;
  }

  const parsedRemarks = rawRemarks
    .map((rawRemark, index): TaskRemark | null => {
      if (!rawRemark || typeof rawRemark !== 'object') {
        return null;
      }

      const remarkRecord = rawRemark as Record<string, unknown>;
      const remarkText =
        typeof remarkRecord.remark === 'string' ? remarkRecord.remark.trim() : '';

      if (!remarkText) {
        return null;
      }

      const remarkId =
        typeof remarkRecord.id === 'string' && remarkRecord.id.trim()
          ? remarkRecord.id
          : `remark_${taskId}_${index}`;
      const employeeId =
        typeof remarkRecord.employeeId === 'string' && remarkRecord.employeeId.trim()
          ? remarkRecord.employeeId
          : 'unknown';
      const employeeName =
        typeof remarkRecord.employeeName === 'string' && remarkRecord.employeeName.trim()
          ? remarkRecord.employeeName.trim()
          : 'Unknown User';
      const remarkTaskId =
        typeof remarkRecord.taskId === 'string' && remarkRecord.taskId.trim()
          ? remarkRecord.taskId
          : taskId;
      const mentionedUserIds = Array.isArray(remarkRecord.mentionedUserIds)
        ? remarkRecord.mentionedUserIds
            .map((value) => String(value || '').trim())
            .filter(Boolean)
        : [];
      const mentionedDisplayNames = Array.isArray(remarkRecord.mentionedDisplayNames)
        ? remarkRecord.mentionedDisplayNames
            .map((value) => String(value || '').trim())
            .filter(Boolean)
        : [];

      return {
        id: remarkId,
        taskId: remarkTaskId,
        employeeId,
        employeeName,
        remark: remarkText,
        timestamp: toTimestampNumber(remarkRecord.timestamp),
        ...(mentionedUserIds.length > 0 ? { mentionedUserIds } : {}),
        ...(mentionedDisplayNames.length > 0 ? { mentionedDisplayNames } : {})
      };
    })
    .filter((remark): remark is TaskRemark => Boolean(remark))
    .sort((left, right) => left.timestamp - right.timestamp);

  return parsedRemarks;
};

type InAppNotification = {
  id: string;
  title: string;
  body: string;
  is_read: boolean;
  created_at: string;
  entity_type?: string | null;
  entity_id?: string | null;
};

type PendingTaskDeepLink = {
  taskId: string;
  openChat: boolean;
  token: number;
};

type PendingApprovalDeepLink = {
  approvalId: string;
  token: number;
};

type NotificationRoute = 'tasks' | 'approvals';

type PendingNotificationNavigation = {
  companyId: string | null;
  taskId: string | null;
  approvalId: string | null;
  route: NotificationRoute | null;
  openChat: boolean;
  token: number;
};

type AvailableCompany = {
  employeeId: string;
  role: Employee['role'];
  companyId: string;
  companyName: string;
  employeeRecord: Employee;
};

const formatNotificationTimeAgo = (createdAt: string): string => {
  const createdTs = Date.parse(createdAt);
  if (!Number.isFinite(createdTs)) return 'Just now';

  const diff = Math.max(0, Date.now() - createdTs);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diff < minute) return 'Just now';
  if (diff < hour) return `${Math.floor(diff / minute)}m ago`;
  if (diff < day) return `${Math.floor(diff / hour)}h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return new Date(createdAt).toLocaleDateString();
};

const getCurrentPathname = (): string => {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname || '/';
};

const getCurrentSearch = (): string => {
  if (typeof window === 'undefined') return '';
  return window.location.search || '';
};

const parseBooleanLikeValue = (value: unknown): boolean => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
};

const normalizeNotificationRoute = (value: unknown): NotificationRoute | null => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'tasks' || normalized === 'approvals') {
    return normalized;
  }
  return null;
};

const parseNotificationNavigationTarget = (source: Record<string, unknown>): Omit<PendingNotificationNavigation, 'token'> | null => {
  const companyId = String(source.company_id ?? source.companyId ?? '').trim() || null;
  const taskId = String(source.task_id ?? source.taskId ?? '').trim() || null;
  const approvalId = String(source.approval_id ?? source.approvalId ?? '').trim() || null;
  const route = normalizeNotificationRoute(source.route);
  const openChat = parseBooleanLikeValue(source.open_chat ?? source.openChat);

  if (!companyId && !taskId && !approvalId && !route) {
    return null;
  }

  return {
    companyId,
    taskId,
    approvalId,
    route: route ?? (approvalId ? 'approvals' : taskId ? 'tasks' : null),
    openChat,
  };
};

const parseNotificationNavigationTargetFromUrl = (
  rawUrl: unknown
): Omit<PendingNotificationNavigation, 'token'> | null => {
  const candidate = String(rawUrl || '').trim();
  if (!candidate) {
    return null;
  }

  try {
    const baseOrigin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://final-test-task-pro.vercel.app';
    const url = new URL(candidate, baseOrigin);

    return parseNotificationNavigationTarget({
      company_id: url.searchParams.get('company_id'),
      route: url.searchParams.get('route'),
      task_id: url.searchParams.get('task_id') || url.searchParams.get('taskId'),
      approval_id: url.searchParams.get('approval_id') || url.searchParams.get('approvalId'),
      open_chat: url.searchParams.get('open_chat') || url.searchParams.get('openChat'),
    });
  } catch {
    return null;
  }
};

const resolveNotificationNavigationTarget = (
  source: Record<string, unknown>,
  rawUrl?: unknown
): Omit<PendingNotificationNavigation, 'token'> | null => {
  const dataTarget = parseNotificationNavigationTarget(source);
  const urlTarget = parseNotificationNavigationTargetFromUrl(rawUrl);

  if (!dataTarget) {
    return urlTarget;
  }

  if (!urlTarget) {
    return dataTarget;
  }

  return {
    companyId: dataTarget.companyId || urlTarget.companyId,
    taskId: dataTarget.taskId || urlTarget.taskId,
    approvalId: dataTarget.approvalId || urlTarget.approvalId,
    route: dataTarget.route || urlTarget.route,
    openChat: dataTarget.openChat || urlTarget.openChat,
  };
};

const readPersistedActiveCompanyId = (mobile?: string | null): string | null => {
  if (typeof window === 'undefined') return null;

  const normalizedMobile = normalizeMobile(mobile);
  const lookupKeys = normalizedMobile
    ? [`${ACTIVE_COMPANY_CACHE_KEY}:${normalizedMobile}`, ACTIVE_COMPANY_CACHE_KEY]
    : [ACTIVE_COMPANY_CACHE_KEY];

  for (const key of lookupKeys) {
    try {
      const value = String(localStorage.getItem(key) || '').trim();
      if (value) return value;
    } catch {
      // Ignore local storage read failures and continue with defaults.
    }
  }

  return null;
};

const persistActiveCompanyId = (mobile: string | null | undefined, companyId: string | null | undefined) => {
  if (typeof window === 'undefined') return;

  const normalizedMobile = normalizeMobile(mobile);
  const normalizedCompanyId = String(companyId || '').trim();
  if (!normalizedCompanyId) return;

  try {
    localStorage.setItem(ACTIVE_COMPANY_CACHE_KEY, normalizedCompanyId);
    if (normalizedMobile) {
      localStorage.setItem(`${ACTIVE_COMPANY_CACHE_KEY}:${normalizedMobile}`, normalizedCompanyId);
    }
  } catch {
    // Ignore local storage write failures so company switching remains functional.
  }
};

const clearCompanyScopedCaches = () => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(EMPLOYEES_CACHE_KEY);
    localStorage.removeItem(TASKS_CACHE_KEY);
    localStorage.removeItem(STAFF_MANAGER_LINKS_CACHE_KEY);
    localStorage.removeItem(TASK_PARTICIPANTS_CACHE_KEY);
  } catch {
    // Ignore local storage clear failures and continue runtime switching.
  }
};

const getFallbackCompanyName = (companyId: string): string => {
  const normalizedCompanyId = String(companyId || '').trim();
  if (!normalizedCompanyId) return 'Workspace';
  return `Workspace ${normalizedCompanyId.slice(0, 8)}`;
};

const loadAvailableCompaniesForEmployee = async (employee: Employee): Promise<AvailableCompany[]> => {
  const employeeMobile = String(employee.mobile || '').trim();
  if (!employeeMobile) {
    return [];
  }

  const { data: employeeRows, error: employeeRowsError } = await supabase
    .from('employees')
    .select('*')
    .eq('mobile', employeeMobile);

  if (employeeRowsError) {
    console.warn('Workspace lookup by mobile failed:', employeeRowsError);
    logSupabaseQueryError(employeeRowsError);
    return [];
  }

  const normalizedRows = Array.isArray(employeeRows)
    ? employeeRows.map((row) => normalizeEmployeeProfile(row as Employee))
    : [];
  if (normalizedRows.length === 0) {
    return [];
  }

  const uniqueCompanyIds = Array.from(
    new Set(
      normalizedRows
        .map((row) => String(row.company_id || '').trim())
        .filter(Boolean)
    )
  );

  let companyNameById = new Map<string, string>();
  if (uniqueCompanyIds.length > 0) {
    const { data: companyRows, error: companyRowsError } = await supabase
      .from('companies')
      .select('id, name')
      .in('id', uniqueCompanyIds);

    if (companyRowsError) {
      console.warn('Workspace company lookup failed:', companyRowsError);
      logSupabaseQueryError(companyRowsError);
    } else {
      companyNameById = new Map(
        (companyRows || []).map((row: any) => [
          String(row?.id || '').trim(),
          String(row?.name || '').trim() || getFallbackCompanyName(String(row?.id || '').trim()),
        ])
      );
    }
  }

  return normalizedRows
    .map((row) => ({
      employeeId: row.id,
      role: normalizeRole(row.role, row.email),
      companyId: String(row.company_id || '').trim() || DEFAULT_COMPANY_ID,
      companyName:
        companyNameById.get(String(row.company_id || '').trim()) ||
        getFallbackCompanyName(String(row.company_id || '').trim()),
      employeeRecord: row,
    }))
    .sort((left, right) => left.companyName.localeCompare(right.companyName));
};

const App: React.FC = () => {
  // --- 1. USER & STATE MANAGEMENT ---
  const {
    currentUser,
    activeEmployeeRecord,
    availableCompanies,
    activeCompanyId,
    isSyncing,
    isCompanySwitching,
    authLoading,
    login,
    logout,
    switchCompany,
    finishCompanySwitch,
  } = useAuthCompany();
  useNotificationDeepLink();

  const [activeTab, setActiveTab] = useState<AppTab>(() => {
    const cachedTab = readPersistedActiveTab(currentUser?.id);
    return resolveActiveTabForRole(cachedTab, currentUser?.role);
  });
  const [tasksTabReselectSignal, setTasksTabReselectSignal] = useState(0);
  const [appReady, setAppReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ title: string, message: string, taskId?: string } | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<number>(Date.now());
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);
  const [isMobileKeyboardActive, setIsMobileKeyboardActive] = useState(false);
  const [userNotifications, setUserNotifications] = useState<InAppNotification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [currentPath, setCurrentPath] = useState<string>(() => getCurrentPathname());
  const [currentSearch, setCurrentSearch] = useState<string>(() => getCurrentSearch());
  const [pendingTaskDeepLink, setPendingTaskDeepLink] = useState<PendingTaskDeepLink | null>(null);
  const [pendingApprovalDeepLink, setPendingApprovalDeepLink] = useState<PendingApprovalDeepLink | null>(null);
  const [pendingNotificationNavigation, setPendingNotificationNavigation] = useState<PendingNotificationNavigation | null>(null);
  const pendingCompanySwitchNavigationRef = useRef<string | null>(null);
  const pendingNotificationNavigationRef = useRef<PendingNotificationNavigation | null>(null);
  const lastForegroundRefreshAtRef = useRef(0);
  const lastRealtimeTasksRefetchAtRef = useRef(0);
  const errorCaptureUserIdRef = useRef<string | null>(null);
  const errorCaptureCompanyIdRef = useRef<string | null>(null);

  const resolvedActiveCompanyId = String(activeCompanyId || activeEmployeeRecord?.company_id || '').trim() || null;
  const resolvedActiveCompanyIdRef = useRef<string | null>(resolvedActiveCompanyId);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isEditableElement = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tagName = target.tagName.toLowerCase();
      return tagName === 'input' || tagName === 'textarea' || target.isContentEditable;
    };

    const syncKeyboardState = () => {
      const isMobileViewport = window.matchMedia('(max-width: 767px)').matches;
      const focusedEditable = isEditableElement(document.activeElement);
      const viewport = window.visualViewport;
      const keyboardLikelyOpen = Boolean(
        viewport &&
        isMobileViewport &&
        window.innerHeight - viewport.height - viewport.offsetTop > 80
      );

      setIsMobileKeyboardActive(isMobileViewport && (focusedEditable || keyboardLikelyOpen));
    };

    const handleFocusOut = () => {
      window.setTimeout(syncKeyboardState, 120);
    };

    syncKeyboardState();
    window.addEventListener('focusin', syncKeyboardState);
    window.addEventListener('focusout', handleFocusOut);
    window.addEventListener('resize', syncKeyboardState);
    window.visualViewport?.addEventListener('resize', syncKeyboardState);
    window.visualViewport?.addEventListener('scroll', syncKeyboardState);

    return () => {
      window.removeEventListener('focusin', syncKeyboardState);
      window.removeEventListener('focusout', handleFocusOut);
      window.removeEventListener('resize', syncKeyboardState);
      window.visualViewport?.removeEventListener('resize', syncKeyboardState);
      window.visualViewport?.removeEventListener('scroll', syncKeyboardState);
    };
  }, []);

  useEffect(() => {
    resolvedActiveCompanyIdRef.current = resolvedActiveCompanyId;
  }, [resolvedActiveCompanyId]);

  useEffect(() => {
    errorCaptureUserIdRef.current = String(activeEmployeeRecord?.id || currentUser?.id || '').trim() || null;
    errorCaptureCompanyIdRef.current =
      String(resolvedActiveCompanyId || activeEmployeeRecord?.company_id || currentUser?.company_id || '').trim() ||
      null;
    setErrorLoggingContext({
      userId: errorCaptureUserIdRef.current,
      companyId: errorCaptureCompanyIdRef.current,
    });
  }, [
    activeEmployeeRecord?.id,
    activeEmployeeRecord?.company_id,
    currentUser?.id,
    currentUser?.company_id,
    resolvedActiveCompanyId,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined' || authLoading) {
      return;
    }

    const previousOnError = window.onerror;

    window.onerror = (msg, src, line, col, err) => {
      void supabase.from('error_logs').insert({
        type: 'frontend',
        message: String(msg),
        stack: err?.stack ?? null,
        user_id: getCurrentUserId() ?? null,
        company_id: getCurrentCompanyId() ?? null,
        context: { src, line, col, page: window.location.pathname }
      });

      if (typeof previousOnError === 'function') {
        return previousOnError.call(window, msg, src, line, col, err);
      }

      return false;
    };
    console.log("error capture registered");

    const handleUnhandledRejection = (e: PromiseRejectionEvent) => {
      void supabase.from('error_logs').insert({
        type: 'frontend',
        message: e.reason?.message ?? String(e.reason),
        stack: e.reason?.stack ?? null,
        user_id: getCurrentUserId() ?? null,
        company_id: getCurrentCompanyId() ?? null,
        context: { page: window.location.pathname }
      });
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.onerror = previousOnError;
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [authLoading]);

  const unreadNotificationCount = userNotifications.filter((item) => !item.is_read).length;
  const isDevAdminRoute = currentPath === DEV_ADMIN_PATH;
  const isDevAdminAuthorized =
    String(activeEmployeeRecord?.email || '').trim().toLowerCase() === DEV_ADMIN_EMAIL;
  const canAccessCrm =
    String(activeEmployeeRecord?.email || currentUser?.email || '').trim().toLowerCase() === DEV_ADMIN_EMAIL;

  const navigateToPath = useCallback((path: string, replace = false) => {
    if (typeof window === 'undefined') return;
    const targetPath = path || '/';
    const currentBrowserPath = window.location.pathname || '/';

    if (replace) {
      window.history.replaceState({}, '', targetPath);
    } else if (currentBrowserPath !== targetPath) {
      window.history.pushState({}, '', targetPath);
    }

    setCurrentPath(targetPath);
    setCurrentSearch(window.location.search || '');
  }, []);

  const syncBrowserLocation = useCallback(() => {
    setCurrentPath(getCurrentPathname());
    setCurrentSearch(getCurrentSearch());
  }, []);

  const clearPendingTaskDeepLink = useCallback(() => {
    setPendingTaskDeepLink(null);
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, '', '/');
      setCurrentPath('/');
      setCurrentSearch('');
    }
  }, []);

  const clearPendingApprovalDeepLink = useCallback(() => {
    setPendingApprovalDeepLink(null);
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, '', '/');
      setCurrentPath('/');
      setCurrentSearch('');
    }
  }, []);

  const queueNotificationNavigation = useCallback((target: Omit<PendingNotificationNavigation, 'token'>) => {
    const existing = pendingNotificationNavigationRef.current;
    if (
      existing &&
      existing.companyId === target.companyId &&
      existing.taskId === target.taskId &&
      existing.approvalId === target.approvalId &&
      existing.route === target.route &&
      existing.openChat === target.openChat
    ) {
      return existing;
    }

    const nextNavigation = { ...target, token: Date.now() };
    setPendingNotificationNavigation(nextNavigation);
    pendingNotificationNavigationRef.current = nextNavigation;
    return nextNavigation;
  }, []);

  const clearNotificationNavigation = useCallback(() => {
    pendingCompanySwitchNavigationRef.current = null;
    setPendingNotificationNavigation(null);
    pendingNotificationNavigationRef.current = null;
  }, []);

  const routeNotificationTarget = useCallback((target: Omit<PendingNotificationNavigation, 'token'>, overrideCurrentCompanyId?: string) => {
    const targetCompanyId = String(target.companyId || '').trim();
    const currentCompanyId = overrideCurrentCompanyId ?? resolvedActiveCompanyIdRef.current ?? '';
    const shouldOpenApprovals =
      target.route === 'approvals' || (!!target.approvalId && target.route !== 'tasks');
    const shouldOpenTasks = target.route === 'tasks' || !!target.taskId;

    if (!activeEmployeeRecord?.id) {
      queueNotificationNavigation(target);
      return;
    }

    if (targetCompanyId && targetCompanyId !== currentCompanyId) {
      if (!availableCompanies.length) {
        queueNotificationNavigation(target);
        return;
      }

      const matchedWorkspace = availableCompanies.find(
        (workspace) => String(workspace.companyId || '').trim() === targetCompanyId
      );

      if (!matchedWorkspace) {
        console.warn('Notification deep-link target company not available:', targetCompanyId);
        clearNotificationNavigation();
        return;
      }

      queueNotificationNavigation(target);

      if (pendingCompanySwitchNavigationRef.current !== targetCompanyId) {
        pendingCompanySwitchNavigationRef.current = targetCompanyId;
        switchCompany(targetCompanyId);
      }
      return;
    }

    if (
      targetCompanyId &&
      pendingCompanySwitchNavigationRef.current === targetCompanyId &&
      isCompanySwitching
    ) {
      queueNotificationNavigation(target);
      return;
    }

    pendingCompanySwitchNavigationRef.current = null;

    navigateToPath('/', true);

    if (shouldOpenApprovals) {
      setActiveTab(AppTab.APPROVALS);
      setPendingTaskDeepLink(null);
      setPendingApprovalDeepLink(
        target.approvalId ? { approvalId: target.approvalId, token: Date.now() } : null
      );
    } else if (shouldOpenTasks) {
      setActiveTab(AppTab.TASKS);
      setPendingApprovalDeepLink(null);
      setPendingTaskDeepLink(
        target.taskId ? { taskId: target.taskId, openChat: target.openChat, token: Date.now() } : null
      );
    }

    clearNotificationNavigation();
  }, [
    activeEmployeeRecord?.id,
    availableCompanies,
    clearNotificationNavigation,
    isCompanySwitching,
    navigateToPath,
    queueNotificationNavigation,
    switchCompany,
  ]);

  const loadUserNotifications = useCallback(async () => {
    const userId = String(activeEmployeeRecord?.id || '').trim();
    if (!userId) {
      setUserNotifications([]);
      return;
    }

    setNotificationsLoading(true);
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('id, title, body, is_read, created_at, entity_type, entity_id')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(30);

      if (error) {
        console.warn('Failed to load notifications:', error);
        logSupabaseQueryError(error);
        return;
      }

      setUserNotifications((data || []) as InAppNotification[]);
    } finally {
      setNotificationsLoading(false);
    }
  }, [activeEmployeeRecord?.id]);

  // --- OneSignal Notification Setup ---
  useNotificationSetup({
    currentEmployee: activeEmployeeRecord,
    userId: activeEmployeeRecord?.id || null,
    userMobile: activeEmployeeRecord?.mobile || null,
    companyId: resolvedActiveCompanyId,
    isLoggedIn: !!activeEmployeeRecord
  });

  // --- 2. UPDATED EMPLOYEES LIST (With Your Number) ---
  const DEFAULT_EMPLOYEES: Employee[] = [
    {
      id: 'emp-admin',
      name: 'Admin User', // Updated Name
      email: 'admin@company.com', // Added email
      mobile: '9000000001',
      role: 'manager'
    },
    { id: 'emp-staff-1', name: 'Staff Member 1', email: 'staff1@company.com', mobile: '8888888888', role: 'staff' },
    { id: 'emp-staff-2', name: 'Staff Member 2', email: 'staff2@company.com', mobile: '7777777777', role: 'staff' }
  ];

  const DEFAULT_TASKS = [
    {
      id: 'task-demo-1',
      description: 'Welcome to your new OpenTask',
      status: 'pending' as TaskStatus,
      priority: 'Medium' as TaskPriority,
      createdAt: Date.now(),
      assignedBy: 'emp-admin',
      assignedTo: 'emp-staff-1'
    }
  ];

  const [employees, setEmployees] = useState<Employee[]>(() => parseCachedArray<Employee>(EMPLOYEES_CACHE_KEY));
  const [staffManagerLinks, setStaffManagerLinks] = useState<StaffManagerLink[]>(() =>
    parseCachedArray<StaffManagerLink>(STAFF_MANAGER_LINKS_CACHE_KEY)
  );
  const [taskParticipants, setTaskParticipants] = useState<TaskParticipant[]>(() =>
    parseCachedArray<TaskParticipant>(TASK_PARTICIPANTS_CACHE_KEY)
  );
  // Load data logic
  const loadInitialData = async (isSilent: boolean = false) => {
    try {
      if (!isSilent) {
        setLoading(true);
      }
      setLoadError(null);

      const activeCompanyIdValue = resolvedActiveCompanyId;

      // Fetch employees from Supabase (company-scoped when available)
      let employeesQuery = supabase
        .from('employees')
        .select('*');

      if (activeCompanyIdValue) {
        employeesQuery = employeesQuery.eq('company_id', activeCompanyIdValue);
      }

      const { data: employeesData, error: employeesError } = await employeesQuery;

      let finalStaffManagerLinks = filterStaffManagerLinksByCompany(
        parseCachedArray<StaffManagerLink>(STAFF_MANAGER_LINKS_CACHE_KEY),
        activeCompanyIdValue
      );

      if (activeCompanyIdValue) {
        const { data: staffManagerLinksData, error: staffManagerLinksError } = await supabase
          .from('staff_manager_links')
          .select('*')
          .eq('company_id', activeCompanyIdValue);

        if (!staffManagerLinksError && Array.isArray(staffManagerLinksData)) {
          finalStaffManagerLinks = staffManagerLinksData as StaffManagerLink[];
        } else if (staffManagerLinksError && !isMissingRelationError(staffManagerLinksError)) {
          console.warn('Staff manager links error:', staffManagerLinksError);
          logSupabaseQueryError(staffManagerLinksError);
        }
      }

      let finalTaskParticipants = filterTaskParticipantsByCompany(
        parseCachedArray<TaskParticipant>(TASK_PARTICIPANTS_CACHE_KEY),
        activeCompanyIdValue
      );

      if (activeCompanyIdValue) {
        const { data: taskParticipantsData, error: taskParticipantsError } = await supabase
          .from('task_participants')
          .select('*')
          .eq('company_id', activeCompanyIdValue);

        if (!taskParticipantsError && Array.isArray(taskParticipantsData)) {
          finalTaskParticipants = filterTaskParticipantsByCompany(
            taskParticipantsData as TaskParticipant[],
            activeCompanyIdValue
          );
        } else if (taskParticipantsError && !isMissingRelationError(taskParticipantsError)) {
          console.warn('Task participants error:', taskParticipantsError);
          logSupabaseQueryError(taskParticipantsError);
        }
      }

      const participantTaskIds = getParticipantTaskIdsForScope(
        finalTaskParticipants,
        buildTaskScopeIds(currentUser, (employeesData || []) as Employee[])
      );

      // Fetch tasks from Supabase with role + company filtering
      let tasksQuery = supabase.from('tasks').select('*');

      if (activeCompanyIdValue) {
        tasksQuery = tasksQuery.eq('company_id', activeCompanyIdValue);
      }
      tasksQuery = tasksQuery.in('status', ['pending', 'in_progress', 'in-progress', 'overdue', 'completed']);
      
      // Apply role-based filtering using all known IDs for the active user profile.
      tasksQuery = applyTaskVisibilityFilter(
        tasksQuery,
        currentUser,
        (employeesData || []) as Employee[],
        participantTaskIds
      );
      
      const { data: tasksData, error: tasksError } = await tasksQuery;
      const filteredTasksData = Array.isArray(tasksData)
        ? filterSuperAdminVisibleTasks(tasksData as DatabaseTask[], currentUser)
        : null;

      // Cache fallback is always tenant-scoped to avoid leaking demo/other-company users.
      const cachedEmployees = filterEmployeesByCompany(
        parseCachedArray<Employee>(EMPLOYEES_CACHE_KEY),
        activeCompanyIdValue
      );
      const cachedTasks = filterSuperAdminVisibleTasks(
        filterTasksByCompany(
          parseCachedArray<DealershipTask>(TASKS_CACHE_KEY),
          activeCompanyIdValue
        ),
        currentUser
      );

      const finalEmployeesBase = (employeesData && employeesData.length > 0)
        ? employeesData
        : (cachedEmployees.length > 0 ? cachedEmployees : []);

      // Transform database tasks to app tasks
      const finalTasks = filteredTasksData
        ? transformTasksToApp(filteredTasksData as DatabaseTask[])
        : (cachedTasks.length > 0 ? cachedTasks : []);
      const activeTasks = attachTaskParticipantsToTasks(finalTasks, finalTaskParticipants);
      const mergedEmployees = mergeCurrentUserIntoEmployees(finalEmployeesBase as Employee[], activeEmployeeRecord).map((employee) => {
        if (employee.role !== 'staff') {
          return employee;
        }

        const managerIds = getManagerIdsForStaff(employee.id, finalStaffManagerLinks, employee.manager_id || null);
        if (!managerIds.length) {
          return employee;
        }

        return {
          ...employee,
          manager_id: managerIds[0],
        };
      });
      const finalEmployees = scopeEmployeesForCurrentUser(
        mergedEmployees,
        activeTasks,
        activeEmployeeRecord,
        finalStaffManagerLinks
      );

      if (employeesError || tasksError) {
        console.warn('using fallback data due to Supabase error');
        if (employeesError) {
          console.warn('Employees error:', employeesError);
          logSupabaseQueryError(employeesError);
        }
        if (tasksError) {
          console.warn('Tasks error:', tasksError);
          logSupabaseQueryError(tasksError);
        }
      }

      localStorage.setItem(EMPLOYEES_CACHE_KEY, JSON.stringify(mergedEmployees));
      localStorage.setItem(STAFF_MANAGER_LINKS_CACHE_KEY, JSON.stringify(finalStaffManagerLinks));
      localStorage.setItem(TASK_PARTICIPANTS_CACHE_KEY, JSON.stringify(finalTaskParticipants));
      if (filteredTasksData) {
        localStorage.setItem(TASKS_CACHE_KEY, JSON.stringify(activeTasks));
      }

      return {
        employees: finalEmployees,
        tasks: activeTasks,
        staffManagerLinks: finalStaffManagerLinks,
        taskParticipants: finalTaskParticipants
      };
    } catch (error) {
      console.error('Supabase connection failed - using cached fallback data');
      const activeCompanyIdValue = resolvedActiveCompanyId;
      const cachedEmployees = filterEmployeesByCompany(
        parseCachedArray<Employee>(EMPLOYEES_CACHE_KEY),
        activeCompanyIdValue
      );
      const cachedTasks = filterTasksByCompany(
        parseCachedArray<DealershipTask>(TASKS_CACHE_KEY),
        activeCompanyIdValue
      );
      const cachedStaffManagerLinks = filterStaffManagerLinksByCompany(
        parseCachedArray<StaffManagerLink>(STAFF_MANAGER_LINKS_CACHE_KEY),
        activeCompanyIdValue
      );
      const cachedTaskParticipants = filterTaskParticipantsByCompany(
        parseCachedArray<TaskParticipant>(TASK_PARTICIPANTS_CACHE_KEY),
        activeCompanyIdValue
      );
      const mergedEmployees = mergeCurrentUserIntoEmployees(cachedEmployees, activeEmployeeRecord);
      const visibleCachedTasks = attachTaskParticipantsToTasks(
        filterSuperAdminVisibleTasks(cachedTasks, currentUser),
        cachedTaskParticipants
      );
      return {
        employees: scopeEmployeesForCurrentUser(
          mergedEmployees,
          visibleCachedTasks,
          activeEmployeeRecord,
          cachedStaffManagerLinks
        ),
        tasks: visibleCachedTasks,
        staffManagerLinks: cachedStaffManagerLinks,
        taskParticipants: cachedTaskParticipants
      };
    } finally {
      setLoading(false);
    }
  };

  const [tasks, setTasks] = useState<DealershipTask[]>(() =>
    parseCachedArray<DealershipTask>(TASKS_CACHE_KEY)
  );
  const tasksRef = useRef<DealershipTask[]>(tasks);
  const recurringReopenInFlightRef = useRef(false);

  // Ref for fetchTasks to prevent infinite loop
  const fetchTasksRef = useRef(null);

  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  // Extract fetchTasks logic as useCallback to prevent stale closures
  const fetchTasks = useCallback(async () => {
    try {
      
      const activeCompanyIdValue = resolvedActiveCompanyId;

      // Fetch employees from Supabase (company-scoped when available)
      let employeesQuery = supabase
        .from('employees')
        .select('*');

      if (activeCompanyIdValue) {
        employeesQuery = employeesQuery.eq('company_id', activeCompanyIdValue);
      }

      const { data: employeesData, error: employeesError } = await employeesQuery;

      let finalTaskParticipants = filterTaskParticipantsByCompany(
        parseCachedArray<TaskParticipant>(TASK_PARTICIPANTS_CACHE_KEY),
        activeCompanyIdValue
      );

      if (activeCompanyIdValue) {
        const { data: taskParticipantsData, error: taskParticipantsError } = await supabase
          .from('task_participants')
          .select('*')
          .eq('company_id', activeCompanyIdValue);

        if (!taskParticipantsError && Array.isArray(taskParticipantsData)) {
          finalTaskParticipants = filterTaskParticipantsByCompany(
            taskParticipantsData as TaskParticipant[],
            activeCompanyIdValue
          );
        } else if (taskParticipantsError && !isMissingRelationError(taskParticipantsError)) {
          console.warn('Task participants fetch error:', taskParticipantsError);
          logSupabaseQueryError(taskParticipantsError);
        }
      }

      const participantTaskIds = getParticipantTaskIdsForScope(
        finalTaskParticipants,
        buildTaskScopeIds(currentUser, (employeesData || []) as Employee[])
      );

      // Fetch tasks from Supabase with role + company filtering
      let tasksQuery = supabase.from('tasks').select('*');

      if (activeCompanyIdValue) {
        tasksQuery = tasksQuery.eq('company_id', activeCompanyIdValue);
      }
      tasksQuery = tasksQuery.in('status', ['pending', 'in_progress', 'in-progress', 'overdue', 'completed']);
      
      // Apply role-based filtering using all known IDs for the active user profile.
      tasksQuery = applyTaskVisibilityFilter(
        tasksQuery,
        currentUser,
        (employeesData || []) as Employee[],
        participantTaskIds
      );
      
      const { data: tasksData, error: tasksError } = await tasksQuery;
      
      if (tasksError) {
        console.error('❌ Failed to fetch tasks:', tasksError);
        logSupabaseQueryError(tasksError);
      } else {
        const filteredTasks = filterSuperAdminVisibleTasks((tasksData || []) as DatabaseTask[], currentUser);
        const freshTasks = attachTaskParticipantsToTasks(
          transformTasksToApp(filteredTasks as DatabaseTask[]),
          finalTaskParticipants
        );
        setTaskParticipants(finalTaskParticipants);
        setTasks(freshTasks);
      }
    } catch (err) {
      console.error('🚨 Unexpected error fetching tasks:', err);
    }
  }, [activeEmployeeRecord?.id, activeEmployeeRecord?.role, currentUser, resolvedActiveCompanyId]); // Keep scoped to active user/company

  // Keep ref updated with latest fetchTasks function
  useEffect(() => {
    fetchTasksRef.current = fetchTasks;
  }, [fetchTasks]);

  useEffect(() => {
    if (!activeEmployeeRecord) {
      setEmployees([]);
      setStaffManagerLinks([]);
      setTaskParticipants([]);
      setTasks([]);
      return;
    }

    // Initial Load for the active logged-in user/company
    const isSilentRefresh = isCompanySwitching;

    loadInitialData(isSilentRefresh).then(data => {
      if (data) {
        setEmployees(data.employees);
        setStaffManagerLinks(data.staffManagerLinks || []);
        setTaskParticipants(data.taskParticipants || []);
        setTasks(data.tasks);
      }
    }).finally(() => {
      if (isSilentRefresh) {
        finishCompanySwitch();
      }
    });
  }, [activeEmployeeRecord?.id, activeEmployeeRecord?.role, resolvedActiveCompanyId, isCompanySwitching, finishCompanySwitch]);

  useEffect(() => {
    if (!authLoading && !activeEmployeeRecord) {
      setLoading(false);
    }
  }, [authLoading, activeEmployeeRecord]);

  useEffect(() => {
    const normalizedTab = resolveActiveTabForRole(activeTab, activeEmployeeRecord?.role);

    if (normalizedTab !== activeTab) {
      setActiveTab(normalizedTab);
      return;
    }

    if (typeof window === 'undefined') return;

    const normalizedUserId = String(activeEmployeeRecord?.id || '').trim();
    const perUserTabKey = normalizedUserId
      ? `${ACTIVE_TAB_CACHE_KEY}:${normalizedUserId}`
      : ACTIVE_TAB_CACHE_KEY;

    try {
      localStorage.setItem(ACTIVE_TAB_CACHE_KEY, normalizedTab);
      localStorage.setItem(perUserTabKey, normalizedTab);
    } catch {
      // Ignore local storage write failures so tab navigation remains functional.
    }
  }, [activeTab, activeEmployeeRecord?.id, activeEmployeeRecord?.role]);

  // --- REALTIME SUBSCRIPTION FOR TASKS ---
  useEffect(() => {
    if (!activeEmployeeRecord?.id) {
      return;
    }

    const companyFilter = resolvedActiveCompanyId ? `company_id=eq.${resolvedActiveCompanyId}` : undefined;
    const taskChangeFilter: { schema: 'public'; table: 'tasks'; filter?: string } = {
      schema: 'public',
      table: 'tasks',
      ...(companyFilter ? { filter: companyFilter } : {}),
    };
    const taskDeleteChangeFilter: { schema: 'public'; table: 'tasks' } = {
      schema: 'public',
      table: 'tasks',
    };
    const taskScopeIdsForRealtime = buildTaskScopeIds(activeEmployeeRecord, employees);
    const participantTaskIdsForRealtime = getParticipantTaskIdsForScope(taskParticipants, taskScopeIdsForRealtime);
    const triggerRealtimeTasksRefetch = () => {
      const now = Date.now();
      if (now - lastRealtimeTasksRefetchAtRef.current < 700) {
        return;
      }
      lastRealtimeTasksRefetchAtRef.current = now;
      const refetchTasks = fetchTasksRef.current as null | (() => Promise<void>);
      if (refetchTasks) {
        void refetchTasks();
      }
    };

    const taskListener = supabase
      .channel(`public:tasks:${resolvedActiveCompanyId || 'all'}:${activeEmployeeRecord.id}`)
      .on('postgres_changes', { event: 'INSERT', ...taskChangeFilter }, async (payload) => {
        try {
          const payloadTaskId = String((payload.new as any)?.id || '');
          if (!payloadTaskId) {
            console.warn('Realtime INSERT payload missing task id:', payload);
            return;
          }

          const payloadRow = (payload.new || {}) as DatabaseTask;
          if (!isTaskVisibleToUser(payloadRow, activeEmployeeRecord, taskScopeIdsForRealtime, participantTaskIdsForRealtime)) {
            triggerRealtimeTasksRefetch();
            return;
          }
          if (isHiddenSelfAssignedTaskForSuperAdmin(payloadRow, activeEmployeeRecord)) {
            return;
          }

          // Apply realtime row immediately so UI updates without waiting for refetch.
          const optimisticTask = attachTaskParticipantsToTasks([transformTaskToApp(payloadRow)], taskParticipants)[0];
          setTasks(prev => upsertTaskAtTop(prev, optimisticTask as DealershipTask));

          // Step 1: Fetch the raw task
          const { data: task, error: taskError } = await supabase
            .from('tasks')
            .select('*')
            .eq('id', payloadTaskId)
            .single();
          
          if (taskError || !task) {
            console.warn('Realtime INSERT: using payload fallback; failed to refetch full task row.', taskError);
            logSupabaseQueryError(taskError);
            return;
          }
          if (isHiddenSelfAssignedTaskForSuperAdmin(task as DatabaseTask, activeEmployeeRecord)) {
            return;
          }

          // Step 2: Fetch assignee if exists
          let assignee = null;
          const taskAssignedTo = (task as any).assignedTo ?? (task as any).assigned_to;
          if (taskAssignedTo) {
            const { data: assigneeData, error: assigneeError } = await supabase
              .from('employees')
              .select('*')
              .eq('id', taskAssignedTo)
              .single();
            
            if (!assigneeError && assigneeData) {
              assignee = assigneeData;
            }
          }

          // Step 3: Fetch assigner if exists
          let assigner = null;
          const taskAssignedBy = (task as any).assignedBy ?? (task as any).assigned_by;
          if (taskAssignedBy) {
            const { data: assignerData, error: assignerError } = await supabase
              .from('employees')
              .select('*')
              .eq('id', taskAssignedBy)
              .single();
            
            if (!assignerError && assignerData) {
              assigner = assignerData;
            }
          }

          // Step 4: Transform database task to app task and combine with user data
          const appTask = attachTaskParticipantsToTasks([transformTaskToApp(task as DatabaseTask)], taskParticipants)[0];
          const richTask = {
            ...appTask,
            assignedTo_user: assignee,
            assigned_by_user: assigner
          };

          setTasks(prev => upsertTaskAtTop(prev, richTask as DealershipTask));

        } catch (err) {
          console.error('🚨 Error in realtime INSERT handler:', err);
          triggerRealtimeTasksRefetch();
        }
      })
      .on('postgres_changes', { event: 'UPDATE', ...taskChangeFilter }, async (payload) => {
        try {
          const payloadTaskId = String((payload.new as any)?.id || '');
          if (!payloadTaskId) {
            console.warn('Realtime UPDATE payload missing task id:', payload);
            return;
          }

          const payloadRow = (payload.new || {}) as DatabaseTask;
          if (!isTaskVisibleToUser(payloadRow, activeEmployeeRecord, taskScopeIdsForRealtime, participantTaskIdsForRealtime)) {
            triggerRealtimeTasksRefetch();
            return;
          }
          if (isHiddenSelfAssignedTaskForSuperAdmin(payloadRow, activeEmployeeRecord)) {
            setTasks((prev) => prev.filter((task) => task.id !== payloadTaskId));
            return;
          }

          // Apply realtime row immediately; later refetch enriches employee lookups.
          const optimisticTask = attachTaskParticipantsToTasks([transformTaskToApp(payloadRow)], taskParticipants)[0];
          setTasks(prev => upsertTaskInPlace(prev, optimisticTask as DealershipTask));

          if (payloadTaskId) {
            const payloadRemarks = normalizeRealtimeRemarks((payload.new as any)?.remarks, payloadTaskId);
            if (payloadRemarks) {
              setTasks((prev) =>
                prev.map((task) =>
                  task.id === payloadTaskId
                    ? { ...task, remarks: payloadRemarks }
                    : task
                )
              );
            }
          }

          // Step 1: Fetch the raw task
          const { data: task, error: taskError } = await supabase
            .from('tasks')
            .select('*')
            .eq('id', payloadTaskId)
            .single();
          
          if (taskError || !task) {
            console.warn('Realtime UPDATE fallback applied; failed to refetch full task row.', taskError);
            logSupabaseQueryError(taskError);
            return;
          }
          if (isHiddenSelfAssignedTaskForSuperAdmin(task as DatabaseTask, activeEmployeeRecord)) {
            setTasks((prev) => prev.filter((existingTask) => existingTask.id !== payloadTaskId));
            return;
          }

          // Step 2: Fetch assignee if exists
          let assignee = null;
          const taskAssignedTo = (task as any).assignedTo ?? (task as any).assigned_to;
          if (taskAssignedTo) {
            const { data: assigneeData, error: assigneeError } = await supabase
              .from('employees')
              .select('*')
              .eq('id', taskAssignedTo)
              .single();
            
            if (!assigneeError && assigneeData) {
              assignee = assigneeData;
            }
          }

          // Step 3: Fetch assigner if exists
          let assigner = null;
          const taskAssignedBy = (task as any).assignedBy ?? (task as any).assigned_by;
          if (taskAssignedBy) {
            const { data: assignerData, error: assignerError } = await supabase
              .from('employees')
              .select('*')
              .eq('id', taskAssignedBy)
              .single();
            
            if (!assignerError && assignerData) {
              assigner = assignerData;
            }
          }

          // Step 4: Transform database task to app task and combine with user data
          const appTask = attachTaskParticipantsToTasks([transformTaskToApp(task as DatabaseTask)], taskParticipants)[0];
          const richTask = {
            ...appTask,
            assignedTo_user: assignee,
            assigned_by_user: assigner
          };

          setTasks(prev => upsertTaskInPlace(prev, richTask as DealershipTask));

        } catch (err) {
          console.error('🚨 Error in realtime UPDATE handler:', err);
          triggerRealtimeTasksRefetch();
        }
      })
      .on('postgres_changes', { event: 'DELETE', ...taskDeleteChangeFilter }, (payload) => {
        const deletedRow = (payload.old || {}) as Record<string, unknown>;
        const deletedTaskId = String(deletedRow.id || '').trim();
        if (!deletedTaskId) {
          console.warn('Realtime DELETE payload missing task id:', payload);
          triggerRealtimeTasksRefetch();
          return;
        }

        const deletedCompanyId = String(deletedRow.company_id || '').trim();
        const currentCompanyId = String(resolvedActiveCompanyId || '').trim();
        if (currentCompanyId && deletedCompanyId && deletedCompanyId !== currentCompanyId) {
          return;
        }

        setTasks((prev) => {
          const nextTasks = prev.filter((task) => task.id !== deletedTaskId);
          return nextTasks.length === prev.length ? prev : nextTasks;
        });
      })
      .subscribe();

    // Cleanup subscription when component unmounts
    return () => {
      supabase.removeChannel(taskListener);
    };
  }, [activeEmployeeRecord?.id, activeEmployeeRecord?.role, activeEmployeeRecord?.auth_user_id, resolvedActiveCompanyId, employees, taskParticipants]);

  useEffect(() => {
    if (!activeEmployeeRecord?.id || !resolvedActiveCompanyId) {
      return;
    }

    const triggerParticipantRefetch = () => {
      const now = Date.now();
      if (now - lastRealtimeTasksRefetchAtRef.current < 700) {
        return;
      }
      lastRealtimeTasksRefetchAtRef.current = now;
      const refetchTasks = fetchTasksRef.current as null | (() => Promise<void>);
      if (refetchTasks) {
        void refetchTasks();
      }
    };

    const participantListener = supabase
      .channel(`public:task_participants:${resolvedActiveCompanyId}:${activeEmployeeRecord.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'task_participants',
          filter: `company_id=eq.${resolvedActiveCompanyId}`,
        },
        () => {
          triggerParticipantRefetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(participantListener);
    };
  }, [activeEmployeeRecord?.id, resolvedActiveCompanyId]);

  const reopenDueRecurringTasks = useCallback(async () => {
    if (!currentUser?.id || recurringReopenInFlightRef.current) {
      return;
    }

    const companyId = String(currentUser.company_id || '').trim();
    const visibleScopeIds = new Set(buildTaskScopeIds(currentUser, employees));
    const nowMs = Date.now();
    const dueTasks = tasksRef.current.filter((task) => {
      if (String(task.status || '').toLowerCase() !== 'completed') {
        return false;
      }

      const recurrence = resolveRecurringFrequencyForTask(task);
      if (!recurrence) {
        return false;
      }

      const nextRaw =
        (task as any).nextRecurrenceNotificationAt ?? (task as any).next_recurrence_notification_at;
      const nextAt = toOptionalTimestampNumber(nextRaw);
      if (!nextAt || nextAt > nowMs) {
        return false;
      }

      if (companyId) {
        const taskCompanyId = String(task.company_id || '').trim();
        if (taskCompanyId && taskCompanyId !== companyId) {
          return false;
        }
      }

      if (visibleScopeIds.size > 0 && currentUser.role !== 'owner' && currentUser.role !== 'super_admin') {
        const assignedTo = String(task.assignedTo || '').trim();
        const assignedBy = String(task.assignedBy || '').trim();
        const visibleToUser = Array.from(visibleScopeIds).some((id) => id === assignedTo || id === assignedBy);
        if (!visibleToUser) {
          return false;
        }
      }

      return true;
    });

    if (!dueTasks.length) {
      return;
    }

    recurringReopenInFlightRef.current = true;
    const reopenedById = new Map<string, number | null>();

    try {
      for (const task of dueTasks) {
        const recurrence = resolveRecurringFrequencyForTask(task);
        if (!recurrence) {
          continue;
        }

        const nextReminderAt = computeNextRecurrenceNotificationAt(nowMs, recurrence);
        const snakePayload: Record<string, unknown> = {
          status: 'pending' as TaskStatus,
          completedAt: null,
          proof: null,
          next_recurrence_notification_at: nextReminderAt,
        };

        let updateQuery = supabase.from('tasks').update(snakePayload).eq('id', task.id);
        if (companyId) {
          updateQuery = updateQuery.eq('company_id', companyId);
        }
        let { error: reopenError } = await updateQuery;

        if (reopenError && isMissingNextRecurrenceNotificationColumnError(reopenError)) {
          const camelPayload: Record<string, unknown> = {
            status: 'pending' as TaskStatus,
            completedAt: null,
            proof: null,
            nextRecurrenceNotificationAt: nextReminderAt,
          };
          let camelQuery = supabase.from('tasks').update(camelPayload).eq('id', task.id);
          if (companyId) {
            camelQuery = camelQuery.eq('company_id', companyId);
          }
          const retryResult = await camelQuery;
          reopenError = retryResult.error;
        }

        if (reopenError) {
          console.warn('Failed to reopen due recurring task:', reopenError);
          logSupabaseQueryError(reopenError);
          continue;
        }

        reopenedById.set(task.id, nextReminderAt);
      }
    } finally {
      recurringReopenInFlightRef.current = false;
    }

    if (!reopenedById.size) {
      return;
    }

    setTasks((prev) =>
      prev.map((task) => {
        if (!reopenedById.has(task.id)) {
          return task;
        }

        return {
          ...task,
          status: 'pending' as TaskStatus,
          completedAt: undefined,
          proof: undefined,
          nextRecurrenceNotificationAt: reopenedById.get(task.id) ?? task.nextRecurrenceNotificationAt ?? null,
        };
      })
    );
  }, [currentUser?.id, currentUser?.role, resolvedActiveCompanyId, employees]);

  useEffect(() => {
    if (!currentUser?.id) {
      return;
    }

    void reopenDueRecurringTasks();

    const intervalId = window.setInterval(() => {
      void reopenDueRecurringTasks();
    }, 60 * 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [currentUser?.id, reopenDueRecurringTasks]);

  // --- APP RESUME LISTENERS FOR TASK SYNC ---
  useEffect(() => {
    if (!currentUser) {
      return;
    }

    const triggerForegroundRefresh = (reason: 'focus' | 'visibility') => {
      const now = Date.now();
      // Browsers often fire both visibility + focus together; collapse to one refresh.
      if (now - lastForegroundRefreshAtRef.current < 1200) {
        return;
      }

      lastForegroundRefreshAtRef.current = now;
      syncBrowserLocation();
      // Keep foreground refresh silent so transient focus changes (e.g. file picker) do not
      // remount the app and wipe in-progress form state.
      loadInitialData(true).then(data => {
        if (data) {
          setEmployees(data.employees);
          setStaffManagerLinks(data.staffManagerLinks || []);
          setTaskParticipants(data.taskParticipants || []);
          setTasks(data.tasks);
        }
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        triggerForegroundRefresh('visibility');
      }
    };

    const handleFocus = () => {
      triggerForegroundRefresh('focus');
    };


    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Cleanup event listeners when component unmounts
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [currentUser?.id, currentUser?.role, resolvedActiveCompanyId, syncBrowserLocation]);

  // --- 3. EFFECTS (Notifications & Auto-Save) ---

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
    setAppReady(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !currentSearch) {
      return;
    }

    const target = parseNotificationNavigationTargetFromUrl(window.location.href);
    if (target) {
      queueNotificationNavigation(target);
    }
  }, [currentSearch, queueNotificationNavigation]);

  useEffect(() => {
    if (typeof window === 'undefined' || !OneSignal?.Notifications?.addEventListener) {
      return;
    }

    const handleNotificationClick = (event: any) => {
      const rawData = event?.notification?.additionalData || event?.notification?.data || {};
      const rawUrl =
        event?.notification?.launchURL ||
        event?.notification?.launchUrl ||
        event?.notification?.url ||
        (rawData as Record<string, unknown>)?.launch_url ||
        (rawData as Record<string, unknown>)?.launchUrl ||
        (rawData as Record<string, unknown>)?.url;
      const target = resolveNotificationNavigationTarget(rawData, rawUrl);
      if (!target) {
        return;
      }

      queueNotificationNavigation(target);
    };

    OneSignal.Notifications.addEventListener('click', handleNotificationClick);

    return () => {
      OneSignal.Notifications.removeEventListener?.('click', handleNotificationClick);
    };
  }, [queueNotificationNavigation]);

  useEffect(() => {
    if (!pendingNotificationNavigation) {
      return;
    }

    const { token: _token, ...target } = pendingNotificationNavigation;
    routeNotificationTarget(target, resolvedActiveCompanyId ?? '');
  }, [
    activeEmployeeRecord?.id,
    availableCompanies.length,
    isCompanySwitching,
    pendingNotificationNavigation,
    resolvedActiveCompanyId,
    routeNotificationTarget,
  ]);

  useEffect(() => {
    const handlePopState = () => {
      syncBrowserLocation();
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [syncBrowserLocation]);

  useEffect(() => {
    if (isDevAdminRoute && currentUser && !isDevAdminAuthorized) {
      navigateToPath('/', true);
    }
  }, [currentUser, isDevAdminAuthorized, isDevAdminRoute, navigateToPath]);

  useEffect(() => {
    if (isDevAdminRoute) {
      return;
    }

    if (currentPath === '/crm' && canAccessCrm) {
      if (activeTab !== AppTab.CRM) {
        setActiveTab(AppTab.CRM);
      }
      return;
    }

    if (currentPath === '/crm' && !canAccessCrm) {
      navigateToPath('/', true);
    }

    if (activeTab === AppTab.CRM) {
      setActiveTab(AppTab.TASKS);
    }
  }, [activeTab, canAccessCrm, currentPath, isDevAdminRoute, navigateToPath]);

  // Auto-Save Tasks
  useEffect(() => {
    if (appReady) {
      localStorage.setItem(TASKS_CACHE_KEY, JSON.stringify(tasks));
    }
  }, [tasks, appReady]);

  useEffect(() => {
    if (appReady) {
      localStorage.setItem(EMPLOYEES_CACHE_KEY, JSON.stringify(employees));
    }
  }, [employees, appReady]);

  useEffect(() => {
    if (appReady) {
      localStorage.setItem(STAFF_MANAGER_LINKS_CACHE_KEY, JSON.stringify(staffManagerLinks));
    }
  }, [staffManagerLinks, appReady]);

  // Notifications Logic
  useEffect(() => {
    if (currentUser && tasks.length > 0) {
      const lastSeen = parseInt(localStorage.getItem(`last_seen_${currentUser.id}`)) || 0;
      const newTasks = tasks.filter(t => t.assignedTo === currentUser.id && t.createdAt > lastSeen && t.status === 'pending');
      
      if (newTasks.length > 0) {
        const lastTask = newTasks[0];
        setNotification({
          title: "New Assignment",
          message: `New task assigned: ${lastTask.description}`,
          taskId: lastTask.id
        });
        // Update lastSeen timestamp to prevent duplicate notifications
        localStorage.setItem(`last_seen_${currentUser.id}`, Date.now().toString());
      }
    }
  }, [currentUser, appReady, tasks]);

  useEffect(() => {
    if (!currentUser?.id) {
      setUserNotifications([]);
      setShowNotificationsPanel(false);
      return;
    }

    void loadUserNotifications();

    const channel = supabase
      .channel(`web-notifications-${currentUser.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${currentUser.id}`,
        },
        () => {
          void loadUserNotifications();
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [currentUser?.id, loadUserNotifications]);

  const markAllNotificationsAsRead = useCallback(async () => {
    if (!currentUser?.id) return;

    setUserNotifications((prev) =>
      prev.map((item) => (item.is_read ? item : { ...item, is_read: true }))
    );

    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', currentUser.id)
      .eq('is_read', false);

    if (error) {
      console.warn('Failed to mark all notifications as read:', error);
      logSupabaseQueryError(error);
      void loadUserNotifications();
    }
  }, [currentUser?.id, loadUserNotifications]);

  const markNotificationAsRead = async (notificationId: string) => {
    if (!currentUser?.id) return;

    setUserNotifications((prev) =>
      prev.map((item) =>
        item.id === notificationId ? { ...item, is_read: true } : item
      )
    );

    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId)
      .eq('user_id', currentUser.id);

    if (error) {
      console.warn('Failed to mark notification as read:', error);
      logSupabaseQueryError(error);
      void loadUserNotifications();
    }
  };

  const clearAllNotifications = useCallback(async () => {
    if (!currentUser?.id || userNotifications.length === 0) return;

    const previousNotifications = userNotifications;
    setUserNotifications([]);

    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('user_id', currentUser.id);

    if (error) {
      console.warn('Failed to clear notifications:', error);
      logSupabaseQueryError(error);
      setUserNotifications(previousNotifications);
      void loadUserNotifications();
      return;
    }

    toast.success('All notifications cleared');
  }, [currentUser?.id, userNotifications, loadUserNotifications]);

  const handleNotificationBannerClick = useCallback((targetTaskId?: string) => {
    setNotification(null);
    if (!targetTaskId) return;

    const targetEl = document.getElementById(`task-card-${targetTaskId}`);
    if (!targetEl) return;

    targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    targetEl.classList.add('flash-highlight');
    window.setTimeout(() => targetEl.classList.remove('flash-highlight'), 1200);
  }, []);

  const handleNotificationItemClick = useCallback((item: InAppNotification) => {
    setShowNotificationsPanel(false);

    const entityType = String(item.entity_type || '').trim().toLowerCase();
    const entityId = String(item.entity_id || '').trim();
    const companyId = String(item.company_id || '').trim() || null;
    const title = String(item.title || '').trim().toLowerCase();

    if (entityType === 'approval') {
      routeNotificationTarget({
        companyId,
        taskId: null,
        approvalId: entityId || null,
        route: 'approvals',
        openChat: false,
      });
      return;
    }

    if (entityType === 'task' && entityId) {
      routeNotificationTarget({
        companyId,
        taskId: entityId,
        approvalId: null,
        route: 'tasks',
        openChat: title.includes('mention'),
      });
      return;
    }

    routeNotificationTarget({
      companyId,
      taskId: null,
      approvalId: null,
      route: 'tasks',
      openChat: false,
    });
  }, [routeNotificationTarget]);

  const toggleNotificationsPanel = () => {
    const isOpening = !showNotificationsPanel;
    setShowNotificationsPanel(isOpening);

    if (isOpening) {
      void markAllNotificationsAsRead();
    }
  };

  const handleTasksTabClick = () => {
    if (currentPath === '/crm') {
      navigateToPath('/');
    }

    if (activeTab === AppTab.TASKS) {
      setTasksTabReselectSignal((prev) => prev + 1);
      return;
    }

    setActiveTab(AppTab.TASKS);
  };

  const handleApprovalsTabClick = () => {
    if (currentPath === '/crm') {
      navigateToPath('/');
    }

    setActiveTab(AppTab.APPROVALS);
  };

  const handleTeamTabClick = () => {
    if (currentPath === '/crm') {
      navigateToPath('/');
    }

    setActiveTab(AppTab.TEAM);
  };

  const handleCrmTabClick = () => {
    if (!canAccessCrm) {
      return;
    }

    navigateToPath('/crm');
    setActiveTab(AppTab.CRM);
  };

  // --- 4. CORE ACTIONS (Add, Delete, Complete) ---

  const awardTaskCompletionPoints = async (task: DealershipTask | undefined): Promise<void> => {
    const assigneeId = String(task?.assignedTo || '').trim();
    if (!assigneeId) {
      return;
    }

    const { error: pointsError } = await supabase.rpc('increment_points', {
      user_id: assigneeId,
      amount: 10,
    });

    if (pointsError) {
      console.error('Failed to award task completion points:', pointsError);
      logSupabaseQueryError(pointsError);
    }
  };

  const notifyTaskCreatorOfCompletion = async (task: DealershipTask | undefined, completedByName: string): Promise<void> => {
    const assignedById = String(task?.assignedBy || '').trim();
    const companyId = String(task?.company_id || '').trim();
    const completedById = String(currentUser?.id || '').trim();

    if (!task || !assignedById || !companyId) {
      return;
    }

    if (completedById && assignedById === completedById) {
      return;
    }

    await sendTaskCompletionNotification(task.description, completedByName, assignedById, companyId, task.id);
  };

  const requestHighPriorityTaskClosure = async (
    task: DealershipTask,
    completion: { proof?: TaskProof | null }
  ): Promise<boolean> => {
    if (!currentUser) {
      return false;
    }

    const approverId = String(task.assignedBy || '').trim();
    if (!approverId) {
      toast.error('No approver is assigned to this high priority task.');
      return false;
    }

    const approvalTitle = `High Priority Task Completion: ${task.description}`;
    const approvalDescription = `${currentUser.name} completed a high priority task and is requesting closure approval`;

    const { data: existingApprovals, error: existingApprovalError } = await supabase
      .from('approvals')
      .select('id, status')
      .eq('task_id', task.id)
      .ilike('title', `${HIGH_PRIORITY_COMPLETION_PREFIX}%`);

    if (existingApprovalError) {
      console.error('Failed to check for an existing completion approval:', existingApprovalError);
      logSupabaseQueryError(existingApprovalError);
    }

    const approvedApproval = existingApprovals?.find(
      (approval) => String(approval.status || '').toUpperCase() === 'APPROVED'
    );
    if (approvedApproval?.id) {
      return finalizeTaskCompletion(task, completion);
    }

    const pendingApproval = existingApprovals?.find((approval) =>
      ['PENDING', 'NEEDS_REVIEW'].includes(String(approval.status || '').toUpperCase())
    );
    if (pendingApproval?.id) {
      if (task.status !== 'pending_approval') {
        const { error: existingTaskUpdateError } = await supabase
          .from('tasks')
          .update({
            status: 'pending_approval' as TaskStatus,
            completedAt: null,
            proof: completion.proof ?? task.proof ?? null,
          })
          .eq('id', task.id);

        if (existingTaskUpdateError) {
          toast.error(`Failed to submit approval request: ${existingTaskUpdateError.message}`);
          return false;
        }

        setTasks((prev) =>
          prev.map((existingTask) =>
            existingTask.id === task.id
              ? {
                  ...existingTask,
                  status: 'pending_approval' as TaskStatus,
                  completedAt: undefined,
                  proof: completion.proof ?? existingTask.proof,
                }
              : existingTask
          )
        );
      }

      toast.warning('Approval already pending');
      return true;
    }

    if (existingApprovals?.length) {
      toast.warning('An approval already exists for this task.');
      return true;
    }

    const requesterEmployeeId = await resolveCurrentEmployeeIdForApproval(currentUser, currentUser.company_id);
    const candidateApproverId = approverId;
    const approverEmployeeId = await resolveEmployeeIdForApproval(candidateApproverId, currentUser.company_id);

    const { data: createdApproval, error: approvalError } = await supabase
      .from('approvals')
      .insert({
        requester_id: requesterEmployeeId,
        approver_id: approverEmployeeId,
        title: approvalTitle,
        description: approvalDescription,
        amount: null,
        status: 'PENDING',
        task_id: task.id,
      })
      .select('id')
      .single();

    if (approvalError) {
      console.error('Failed to create high priority completion approval:', approvalError);
      logSupabaseQueryError(approvalError);
      toast.error(`Failed to submit approval request: ${approvalError.message}`);
      return false;
    }

    const taskUpdatePayload: Record<string, unknown> = {
      status: 'pending_approval' as TaskStatus,
      completedAt: null,
      proof: completion.proof ?? task.proof ?? null,
    };

    const { error: taskUpdateError } = await supabase
      .from('tasks')
      .update(taskUpdatePayload)
      .eq('id', task.id);

    if (taskUpdateError) {
      console.error('Failed to mark task as pending approval:', taskUpdateError);
      logSupabaseQueryError(taskUpdateError);
      await supabase.from('approvals').delete().eq('id', createdApproval.id);
      toast.error(`Failed to submit approval request: ${taskUpdateError.message}`);
      return false;
    }

    setTasks((prev) =>
      prev.map((existingTask) =>
        existingTask.id === task.id
          ? {
              ...existingTask,
              status: 'pending_approval' as TaskStatus,
              completedAt: undefined,
              proof: completion.proof ?? existingTask.proof,
            }
          : existingTask
      )
    );

    toast.success('Sent for approval');
    return true;
  };

  const closePendingExtensionApprovalsForCompletedTask = async (taskId: string): Promise<void> => {
    const normalizedTaskId = String(taskId || '').trim();
    if (!normalizedTaskId) return;

    const { error: approvalError } = await supabase
      .from('approvals')
      .update({ status: 'REJECTED' })
      .eq('task_id', normalizedTaskId)
      .ilike('title', `${EXTENSION_REQUEST_PREFIX}%`)
      .in('status', ['PENDING', 'NEEDS_REVIEW']);

    if (approvalError) {
      console.warn('Failed to close stale deadline extension approvals:', approvalError);
      logSupabaseQueryError(approvalError);
    }
  };

  const finalizeTaskCompletion = async (
    task: DealershipTask,
    completion: { proof?: TaskProof | null }
  ): Promise<boolean> => {
    const completionTimestamp = getLatestTaskProofTimestamp(completion.proof) || Date.now();

    const completionPayload: Record<string, unknown> = {
      status: 'completed' as TaskStatus,
      completedAt: completionTimestamp,
      proof: completion.proof ?? task.proof ?? null,
    };

    if (String(task.extensionStatus || '').toUpperCase() === 'REQUESTED') {
      completionPayload.extension_status = 'REJECTED';
    }

    const { error: taskError } = await supabase
      .from('tasks')
      .update(completionPayload)
      .eq('id', task.id);

    if (taskError) {
      console.error('Error completing task:', taskError);
      logSupabaseQueryError(taskError);
      toast.error(`Failed to complete task: ${taskError.message}`);
      return false;
    }

    const completedTaskPatch: Partial<DealershipTask> = {
      status: 'completed' as TaskStatus,
      completedAt: completionTimestamp,
      proof: completion.proof ?? task.proof ?? undefined,
    };

    if (String(task.extensionStatus || '').toUpperCase() === 'REQUESTED') {
      completedTaskPatch.extensionStatus = 'REJECTED';
    }

    setTasks((prev) =>
      upsertTaskInPlace(prev, {
        ...task,
        ...completedTaskPatch,
      } as DealershipTask)
    );

    await closePendingExtensionApprovalsForCompletedTask(task.id);
    await awardTaskCompletionPoints(task);
    if (currentUser) {
      void notifyTaskCreatorOfCompletion(task, currentUser.name);
    }

    return true;
  };

  const addTask = async (
    description: string,
    assignedTo?: string,
    parentTaskId?: string,
    deadline?: number,
    requirePhoto?: boolean,
    taskType: TaskType = 'one_time',
    recurrenceFrequency?: RecurrenceFrequency | null,
    recurrenceTime?: string | null,
    priority: TaskPriority = 'Medium',
    participantIds: string[] = []
  ) => {
    if (!currentUser) return;
    if (!description.trim()) return;

    const normalizedTaskType: TaskType = taskType === 'recurring' ? 'recurring' : 'one_time';
    const normalizedPriority: TaskPriority =
      priority === 'High' || priority === 'Low' ? priority : 'Medium';
    const normalizedRecurrenceFrequency: RecurrenceFrequency | null =
      normalizedTaskType === 'recurring' &&
      (recurrenceFrequency === 'daily' || recurrenceFrequency === 'weekly' || recurrenceFrequency === 'monthly')
        ? recurrenceFrequency
        : null;
    const normalizedRecurrenceTime =
      normalizedTaskType === 'recurring' && /^\d{2}:\d{2}$/.test(String(recurrenceTime || ''))
        ? String(recurrenceTime)
        : null;

    // Enforce recurrence requirements in app layer before DB constraints are evaluated.
    if (normalizedTaskType === 'recurring' && !normalizedRecurrenceFrequency) {
      toast.error('Select a recurrence frequency for recurring tasks.');
      return;
    }

    const now = Date.now();
    const newTask: DealershipTask = {
      id: `task-${now}`,
      description,
      status: 'pending',
      priority: normalizedPriority,
      taskType: normalizedTaskType,
      recurrenceFrequency: normalizedRecurrenceFrequency,
      recurrenceTime: normalizedRecurrenceTime,
      nextRecurrenceNotificationAt:
        normalizedTaskType === 'recurring'
          ? (
              calculateFirstResurfaceAt(normalizedRecurrenceTime || '') ??
              computeNextRecurrenceNotificationAt(now, normalizedRecurrenceFrequency)
            )
          : null,
      createdAt: now,
      deadline: deadline,
      requirePhoto: requirePhoto || false,
      assignedTo: assignedTo === 'none' ? undefined : assignedTo,
      assignedBy: currentUser.id,
      parentTaskId: parentTaskId,
      company_id: currentUser.company_id || '00000000-0000-0000-0000-000000000001' // Use user's company_id or default
    };

    const normalizedParticipantIds = Array.from(
      new Set(
        (participantIds || [])
          .map((id) => String(id || '').trim())
          .filter(
            (id) =>
              Boolean(id) &&
              id !== String(newTask.assignedTo || '').trim() &&
              id !== String(currentUser.id || '').trim()
          )
      )
    );

    try {
      // Transform app task to database format before inserting
      const dbTask = transformTaskToDB(newTask);

      const insertTaskPayload = async (payload: Record<string, any>) =>
        supabase
          .from('tasks')
          .insert([payload])
          .select('*')
          .maybeSingle();

      // Insert task with schema-tolerant retry order:
      // 1) snake_case recurrence fields
      // 2) camelCase recurrence fields
      // 3) remove next recurrence field
      // 4) remove recurrence fields entirely (legacy fallback)
      let insertPayload = dbTask as unknown as Record<string, any>;
      let { data, error } = await insertTaskPayload(insertPayload);

      if (error && isMissingTaskRecurrenceColumnError(error)) {
        insertPayload = toCamelTaskRecurrencePayload(insertPayload);
        const camelRetryResult = await insertTaskPayload(insertPayload);
        data = camelRetryResult.data as any;
        error = camelRetryResult.error as any;
      }

      if (error && isMissingNextRecurrenceNotificationColumnError(error)) {
        console.warn('Retrying task insert without next recurrence notification field...');
        insertPayload = stripTaskNextRecurrenceField(insertPayload);
        const nextRetryResult = await insertTaskPayload(insertPayload);
        data = nextRetryResult.data as any;
        error = nextRetryResult.error as any;

        if (!error) {
          toast.warning('Task saved, but recurring reminder scheduling needs DB migration to be fully available.');
        }
      }

      if (error && isMissingTaskRecurrenceColumnError(error)) {
        console.warn('Retrying task insert without recurrence fields...');
        insertPayload = stripTaskRecurrenceFields(insertPayload);
        const legacyResult = await insertTaskPayload(insertPayload);
        data = legacyResult.data as any;
        error = legacyResult.error as any;

        if (!error) {
          toast.warning('Task saved, but recurrence options need DB migration to be fully available.');
        }
      }

      if (error) {
        console.error('Error adding task to database:', error);
        logSupabaseQueryError(error);
        toast.error(`Failed to save task: ${error.message}`);
        return;
      }

      if (!data) {
        // Some RLS setups allow INSERT but block RETURNING rows. Treat as success and sync tasks.
        if (normalizedParticipantIds.length > 0) {
          console.warn('Task created without RETURNING row; collaborator rows could not be inserted because task id was unavailable.');
        }
        const refetchTasks = fetchTasksRef.current as null | (() => Promise<void>);
        if (refetchTasks) {
          void refetchTasks();
        }
        return;
      }

      // Transform returned database task back to app format
      const appTask = transformTaskToApp(data as DatabaseTask);
      setTasks(prev => upsertTaskAtTop(prev, appTask));

      if (normalizedParticipantIds.length > 0) {
        const participantRows = buildTaskParticipantRows(
          appTask.id,
          normalizedParticipantIds,
          currentUser.id,
          appTask.company_id || currentUser.company_id || DEFAULT_COMPANY_ID
        );

        if (participantRows.length > 0) {
          const { error: participantInsertError } = await supabase
            .from('task_participants')
            .insert(participantRows);

          if (participantInsertError) {
            console.error('Failed to insert task participants after task creation:', participantInsertError);
            logSupabaseQueryError(participantInsertError);
          } else {
            setTaskParticipants((prev) => {
              const nextRows = participantRows.reduce(
                (rows, row) => upsertTaskParticipantRows(rows, row),
                prev
              );
              setTasks((existingTasks) => attachTaskParticipantsToTasks(existingTasks, nextRows));
              return nextRows;
            });
          }
        }
      }

      // Notification is secondary; we fire it in the background and DON'T await it
      // to ensure the UI remains responsive and the function completes successfully.
      void (async () => {
        try {
          if (appTask.assignedTo) {
            const assignedEmployee = employees.find((employee) => employee.id === appTask.assignedTo);
            
            // Send Push Notification
            await sendTaskAssignmentNotification(
              appTask.description,
              assignedEmployee?.name || 'Team Member',
              currentUser.name,
              appTask.assignedTo,
              appTask.company_id || currentUser.company_id || DEFAULT_COMPANY_ID,
              appTask.id
            );

          }
        } catch (notiError) {
          console.error('Background notification dispatch failed:', notiError);
        }
      })();
    } catch (error) {
      console.error('Error adding task:', error);
      toast.error(`Failed to save task: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const delegateTaskToParticipant = async (taskId: string, participantId: string): Promise<void> => {
    if (!currentUser) {
      return;
    }

    const normalizedTaskId = String(taskId || '').trim();
    const normalizedParticipantId = String(participantId || '').trim();
    if (!normalizedTaskId || !normalizedParticipantId) {
      toast.error('Choose a collaborator to continue.');
      return;
    }

    const task = tasks.find((item) => String(item.id || '').trim() === normalizedTaskId);
    if (!task) {
      toast.error('Task not found.');
      return;
    }

    const existingParticipantIds = new Set(getTaskParticipantUserIds(task));
    if (normalizedParticipantId === String(task.assignedTo || '').trim()) {
      toast.error('This person is already the primary assignee.');
      return;
    }
    if (normalizedParticipantId === String(task.assignedBy || '').trim()) {
      toast.error('This person is already part of the task thread.');
      return;
    }
    if (existingParticipantIds.has(normalizedParticipantId)) {
      toast.error('This collaborator is already added to the task.');
      return;
    }

    const companyId =
      String(task.company_id || currentUser.company_id || resolvedActiveCompanyId || DEFAULT_COMPANY_ID).trim() ||
      DEFAULT_COMPANY_ID;
    const delegatedEmployee = employees.find((employee) => employee.id === normalizedParticipantId);
    const assigneeEmployee = employees.find((employee) => employee.id === task.assignedTo);
    const delegatedEmployeeName = delegatedEmployee?.name?.trim() || 'A teammate';
    const primaryAssigneeName = assigneeEmployee?.name?.trim() || currentUser.name?.trim() || 'A teammate';
    const taskTitle = String(task.description || 'Task').trim() || 'Task';

    const participantRow: TaskParticipant = {
      task_id: normalizedTaskId,
      user_id: normalizedParticipantId,
      added_by: currentUser.id,
      added_at: new Date().toISOString(),
      role: 'delegated',
      company_id: companyId,
    };

    const { error } = await supabase
      .from('task_participants')
      .upsert(participantRow, { onConflict: 'task_id,user_id' });

    if (error) {
      if (isMissingRelationError(error)) {
        toast.error('Task collaborators need a database migration before delegation can be used.');
        return;
      }

      console.error('Failed to add task participant:', error);
      logSupabaseQueryError(error);
      toast.error(`Failed to add collaborator: ${error.message}`);
      return;
    }

    const nextParticipants = upsertTaskParticipantRows(taskParticipants, participantRow);
    setTaskParticipants(nextParticipants);
    setTasks((prev) =>
      attachTaskParticipantsToTasks(
        prev.map((existingTask) => (existingTask.id === normalizedTaskId ? { ...existingTask } : existingTask)),
        nextParticipants
      )
    );

    const creatorNotificationBody = `${primaryAssigneeName} delegated '${taskTitle}' to ${delegatedEmployeeName}`;
    const collaboratorNotificationBody = `${currentUser.name || primaryAssigneeName} added you to '${taskTitle}' as a collaborator`;

    if (task.assignedBy && String(task.assignedBy).trim() !== currentUser.id) {
      void sendTaskCollaborationNotification(
        'Task delegated',
        creatorNotificationBody,
        task.assignedBy,
        companyId,
        normalizedTaskId
      );
    }

    void sendTaskCollaborationNotification(
      'Added to task thread',
      collaboratorNotificationBody,
      normalizedParticipantId,
      companyId,
      normalizedTaskId
    );

    toast.success(`${delegatedEmployeeName} added to the task thread.`);
  };

  const startTask = async (taskId: string) => {
    try {
      // Update task status in Supabase
      const { error } = await supabase
        .from('tasks')
        .update({ status: 'in-progress' as TaskStatus })
        .eq('id', taskId);

      if (error) {
        console.error('Error starting task:', error);
        logSupabaseQueryError(error);
        toast.error(`Failed to update task: ${error.message}`);
        return;
      }

      // Update local state
      setTasks(prev => prev.map(t =>
        t.id === taskId
          ? { ...t, status: 'in-progress' as TaskStatus }
          : t
      ));
    } catch (error) {
      console.error('Error starting task:', error);
      toast.error(`Failed to update task: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const reopenTask = async (taskId: string) => {
    try {
      // Update task status in Supabase
      const { error } = await supabase
        .from('tasks')
        .update({
          status: 'pending' as TaskStatus,
          completedAt: undefined,
          proof: undefined
        })
        .eq('id', taskId);

      if (error) {
        console.error('Error reopening task:', error);
        logSupabaseQueryError(error);
        toast.error(`Failed to update task: ${error.message}`);
        return;
      }

      // Update local state
      setTasks(prev => prev.map(t =>
        t.id === taskId
          ? { ...t, status: 'pending' as TaskStatus, completedAt: undefined, proof: undefined }
          : t
      ));
    } catch (error) {
      console.error('Error reopening task:', error);
      toast.error(`Failed to update task: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const completeTask = async (taskId: string, proofData: TaskProof) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) {
        toast.error('Task not found.');
        return;
      }

      if (
        normalizeTaskPriority(task) === 'High' &&
        (currentUser?.role === 'staff' || currentUser?.role === 'manager')
      ) {
        await requestHighPriorityTaskClosure(task, { proof: proofData });
        return;
      }

      await finalizeTaskCompletion(task, { proof: proofData });
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error(`Failed to complete task: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const saveTaskProof = async (taskId: string, proofData: TaskProof) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) {
        toast.error('Task not found.');
        return;
      }

      const { error } = await supabase
        .from('tasks')
        .update({ proof: proofData })
        .eq('id', taskId);

      if (error) {
        console.error('Error saving task proof:', error);
        logSupabaseQueryError(error);
        toast.error(`Failed to save proof photos: ${error.message}`);
        return;
      }

      setTasks(prev =>
        prev.map(t =>
          t.id === taskId
            ? { ...t, proof: proofData }
            : t
        )
      );
    } catch (error) {
      console.error('Error saving task proof:', error);
      toast.error(`Failed to save proof photos: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const completeTaskWithoutPhoto = async (taskId: string) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) {
        toast.error('Task not found.');
        return;
      }

      if (
        normalizeTaskPriority(task) === 'High' &&
        (currentUser?.role === 'staff' || currentUser?.role === 'manager')
      ) {
        await requestHighPriorityTaskClosure(task, { proof: null });
        return;
      }

      await finalizeTaskCompletion(task, { proof: null });
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error(`Failed to complete task: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const reassignTask = async (taskId: string, newAssigneeId: string) => {
    try {
      // Update task assignment in Supabase using camelCase
      const { error } = await supabase
        .from('tasks')
        .update({ assignedTo: newAssigneeId })
        .eq('id', taskId);

      if (error) {
        console.error('Error reassigning task:', error);
        logSupabaseQueryError(error);
        toast.error(`Failed to reassign task: ${error.message}`);
        return;
      }

      // Update local state
      setTasks(prev => prev.map(t =>
        t.id === taskId ? { ...t, assignedTo: newAssigneeId } : t
      ));
    } catch (error) {
      console.error('Error reassigning task:', error);
      toast.error(`Failed to reassign task: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const deleteTask = async (taskId: string) => {
    try {
      // Delete task from Supabase
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId);
      
      if (error) {
        console.error('Error deleting task:', error);
        logSupabaseQueryError(error);
        toast.error(`Failed to delete task: ${error.message}`);
        return;
      }

      // Update local state
      const subTasks = tasks.filter(t => t.parentTaskId === taskId);
      setTasks(prev => prev.filter(t =>
        t.id !== taskId && !subTasks.some(st => st.id === t.id)
      ));
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(`Failed to delete task: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const updateTaskRemarks = (taskId: string, remarks: TaskRemark[]) => {
    setTasks(prev =>
      prev.map(task =>
        task.id === taskId
          ? { ...task, remarks }
          : task
      )
    );
  };

  const updateStaffManagers = async (staffId: string, managerIds: string[]): Promise<boolean> => {
    if (!currentUser) {
      return false;
    }

    const companyId = String(currentUser.company_id || DEFAULT_COMPANY_ID).trim();
    const normalizedStaffId = String(staffId || '').trim();
    if (!normalizedStaffId) {
      return false;
    }

    const normalizedManagerIds = Array.from(
      new Set(
        (managerIds || [])
          .map((managerId) => String(managerId || '').trim())
          .filter(Boolean)
      )
    );


    try {
      let staffManagerLinksUnavailable = false;

      // Delete existing links first
      const { error: deleteLinksError } = await supabase
        .from('staff_manager_links')
        .delete()
        .eq('company_id', companyId)
        .eq('staff_id', normalizedStaffId);

      if (deleteLinksError && !isMissingRelationError(deleteLinksError)) {
        console.error('Failed to clear existing staff-manager links:', deleteLinksError);
        logSupabaseQueryError(deleteLinksError);
        toast.error(`Failed to update manager links: ${deleteLinksError.message}`);
        return false;
      }

      if (deleteLinksError && isMissingRelationError(deleteLinksError)) {
        staffManagerLinksUnavailable = true;
        console.warn('staff_manager_links table not available, falling back to employees.manager_id only.');
      }

      // Small delay to ensure delete is committed before insert
      await new Promise(resolve => setTimeout(resolve, 100));

      // Insert all new links
      if (normalizedManagerIds.length > 0 && !staffManagerLinksUnavailable) {
        const rows = normalizedManagerIds.map((managerId) => ({
          company_id: companyId,
          staff_id: normalizedStaffId,
          manager_id: managerId,
        }));


        const { data: insertedData, error: upsertLinksError } = await supabase
          .from('staff_manager_links')
          .upsert(rows, { onConflict: 'company_id,staff_id,manager_id' })
          .select();

        if (upsertLinksError && !isMissingRelationError(upsertLinksError)) {
          console.error('Failed to upsert staff-manager links:', upsertLinksError);
          logSupabaseQueryError(upsertLinksError);
          toast.error(`Failed to update manager links: ${upsertLinksError.message}`);
          return false;
        }

        if (upsertLinksError && isMissingRelationError(upsertLinksError)) {
          staffManagerLinksUnavailable = true;
          console.warn('staff_manager_links table not available during upsert, falling back to employees.manager_id only.');
        }

      }

      // Update primary manager in employees table
      const primaryManagerId = normalizedManagerIds[0] || null;
      const { error: updateEmployeeError } = await supabase
        .from('employees')
        .update({ manager_id: primaryManagerId })
        .eq('id', normalizedStaffId)
        .eq('company_id', companyId);

      if (updateEmployeeError && !isMissingColumnError(updateEmployeeError)) {
        console.warn('Failed to update employee.manager_id after manager-link sync:', updateEmployeeError);
        logSupabaseQueryError(updateEmployeeError);
        toast.error(`Failed to update manager assignment: ${updateEmployeeError.message}`);
        return false;
      }

      // Update local state immediately
      const nowIso = new Date().toISOString();
      setStaffManagerLinks((prev) => {
        const remaining = prev.filter(
          (link) =>
            !(
              String(link.company_id || '').trim() === companyId &&
              String(link.staff_id || '').trim() === normalizedStaffId
            )
        );

        const nextLinks = normalizedManagerIds.map((managerId) => ({
          company_id: companyId,
          staff_id: normalizedStaffId,
          manager_id: managerId,
          created_at: nowIso,
          updated_at: nowIso,
        }));

        const newLinks = staffManagerLinksUnavailable ? remaining : [...remaining, ...nextLinks];
        return newLinks;
      });

      // Also update employees state with the new manager_id
      setEmployees((prev) =>
        prev.map((employee) =>
          employee.id === normalizedStaffId
            ? { ...employee, manager_id: primaryManagerId }
            : employee
        )
      );

      // Force a refresh to ensure all managers see the updated staff list
      if (!staffManagerLinksUnavailable) {
        setTimeout(async () => {
          const { data: refreshedLinks } = await supabase
            .from('staff_manager_links')
            .select('*')
            .eq('company_id', companyId)
            .eq('staff_id', normalizedStaffId);
          
          if (refreshedLinks && refreshedLinks.length > 0) {
            setStaffManagerLinks((prev) => {
              const otherLinks = prev.filter(
                (link) =>
                  !(
                    String(link.company_id || '').trim() === companyId &&
                    String(link.staff_id || '').trim() === normalizedStaffId
                  )
              );
              return [...otherLinks, ...(refreshedLinks as StaffManagerLink[])];
            });
          }
        }, 500);
      }

      if (staffManagerLinksUnavailable) {
        toast.warning('Multi-manager links table not found. Saved primary manager assignment only.');
      } else {
      }

      return true;
    } catch (error) {
      console.error('Unexpected error updating staff-manager links:', error);
      toast.error(`Failed to update manager links: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return false;
    }
  };

  const toIndianE164 = (rawMobile: string): string => {
    let digits = String(rawMobile || '').replace(/\D/g, '');
    digits = digits.replace(/^0+/, '');
    if (digits.startsWith('91') && digits.length > 10) {
      digits = digits.slice(2);
    }
    if (digits.length > 10) {
      digits = digits.slice(-10);
    }
    return `+91${digits}`;
  };

  const addEmployee = async (
    name: string,
    mobile: string,
    role: UserRole = 'staff',
    managerId?: string | null
  ) => {
    if (!currentUser) return;

    const formattedMobile = toIndianE164(mobile);
    const mobileDigits = formattedMobile.replace(/^\+91/, '');
    if (mobileDigits.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    const newEmployee: Employee = {
      id: `emp-${Date.now()}`,
      name,
      mobile: formattedMobile,
      role,
      email: `${formattedMobile}@taskpro.local`,
      company_id: currentUser.company_id || DEFAULT_COMPANY_ID,
      manager_id:
        role === 'staff'
          ? (managerId || (currentUser.role === 'manager' ? currentUser.id : null))
          : null
    };


    // Add to local state immediately for instant UI feedback
    setEmployees(prev => [...prev, newEmployee]);

    try {
      const { error } = await supabase
        .from('employees')
        .insert([newEmployee]);

      if (error) {
        console.error('Error adding employee to database:', error);
        logSupabaseQueryError(error);
        toast.error(`Database Error: ${error.message}. Employee added locally.`);
      }
    } catch (error) {
      console.error('Unexpected error adding employee:', error);
      toast.error(`Error: ${error instanceof Error ? error.message : 'Unknown error'}. Employee added locally.`);
    }
  };

  const removeEmployee = async (id: string): Promise<boolean> => {
    const normalizedEmployeeId = String(id || '').trim();
    const employeeToDelete = employees.find(
      (employee) => String(employee.id || '').trim() === normalizedEmployeeId
    );
    if (!employeeToDelete) {
      toast.error('Employee not found.');
      return false;
    }

    const normalizedCurrentUserId = String(currentUser?.id || '').trim();
    if (String(employeeToDelete.id || '').trim() === normalizedCurrentUserId) {
      toast.error('You cannot delete your own account.');
      return false;
    }

    const resolvedCompanyId = String(
      employeeToDelete.company_id || currentUser?.company_id || DEFAULT_COMPANY_ID
    ).trim();

    const isSuperAdminOrOwner =
      currentUser?.role === 'super_admin' || currentUser?.role === 'owner';
    const isManagerLinkedViaJunction =
      currentUser?.role === 'manager' &&
      employeeToDelete.role === 'staff' &&
      staffManagerLinks.some(
        (link) =>
          String(link.staff_id || '').trim() === normalizedEmployeeId &&
          String(link.manager_id || '').trim() === normalizedCurrentUserId &&
          (!String(link.company_id || '').trim() || String(link.company_id || '').trim() === resolvedCompanyId)
      );
    const isManagerDeletingOwnStaff =
      currentUser?.role === 'manager' &&
      employeeToDelete.role === 'staff' &&
      String(employeeToDelete.manager_id || '').trim() === normalizedCurrentUserId;

    if (!isSuperAdminOrOwner && !isManagerDeletingOwnStaff && !isManagerLinkedViaJunction) {
      toast.error('You can only delete staff members from your own team.');
      return false;
    }

    try {
      const { error: linksDeleteError } = await supabase
        .from('staff_manager_links')
        .delete()
        .eq('company_id', resolvedCompanyId)
        .or(`staff_id.eq.${normalizedEmployeeId},manager_id.eq.${normalizedEmployeeId}`);

      if (linksDeleteError && !isMissingRelationError(linksDeleteError)) {
        console.warn('Error removing employee manager links:', linksDeleteError);
        logSupabaseQueryError(linksDeleteError);
      }

      const { error } = await supabase
        .from('employees')
        .delete()
        .eq('id', normalizedEmployeeId);

      if (error) {
        console.error('Error removing employee from database:', error);
        logSupabaseQueryError(error);
        toast.error('Could not delete this employee. Please try again.');
        return false;
      }

      const { data: remainingEmployee, error: verifyDeleteError } = await supabase
        .from('employees')
        .select('id')
        .eq('id', normalizedEmployeeId)
        .maybeSingle();

      if (verifyDeleteError) {
        console.error('Error verifying employee delete:', verifyDeleteError);
        logSupabaseQueryError(verifyDeleteError);
        toast.error('Could not verify this employee was deleted. Please refresh and try again.');
        return false;
      }

      if (remainingEmployee) {
        console.error('Employee delete did not remove any rows:', {
          employeeId: normalizedEmployeeId,
          companyId: resolvedCompanyId,
        });
        toast.error('Could not delete this employee from the database. Please refresh and try again.');
        return false;
      }

      const nextEmployees = employees.filter((employee) => String(employee.id || '').trim() !== normalizedEmployeeId);
      const nextLinks = staffManagerLinks.filter(
        (link) =>
          String(link.staff_id || '').trim() !== normalizedEmployeeId &&
          String(link.manager_id || '').trim() !== normalizedEmployeeId
      );

      localStorage.setItem(EMPLOYEES_CACHE_KEY, JSON.stringify(nextEmployees));
      localStorage.setItem(STAFF_MANAGER_LINKS_CACHE_KEY, JSON.stringify(nextLinks));

      setEmployees(nextEmployees);
      setStaffManagerLinks(nextLinks);
      const freshData = await loadInitialData(true);
      if (freshData) {
        setEmployees(freshData.employees);
        setStaffManagerLinks(freshData.staffManagerLinks || []);
      }
      toast.success('Employee deleted successfully.');
      return true;
    } catch (error) {
      console.error('Unexpected error removing employee:', error);
      toast.error('Could not delete this employee. Please try again.');
      return false;
    }
  };

  const handleLogin = async (user: Employee) => {
    await login(user);
    setActiveTab(resolveActiveTabForRole(readPersistedActiveTab(user.id), user.role));
  };

  const handleLogout = async () => {
    setShowNotificationsPanel(false);
    setUserNotifications([]);
    await logout();
  };

  // --- 5. RENDER UI ---

  if (!appReady) {
    return (
      <ErrorBoundary>
        <div className="min-h-screen w-full bg-slate-50 flex flex-col items-center justify-center text-slate-900 p-8 overflow-x-hidden">
          <Loader2 className="w-12 h-12 text-slate-800 animate-spin mb-4" />
          <p className="text-xs font-black uppercase tracking-[0.3em] text-slate-500">Starting App...</p>
        </div>
      </ErrorBoundary>
    );
  }

  if (authLoading || loading) {
    return (
      <ErrorBoundary>
        <div className="flex flex-col min-h-screen w-full max-w-md sm:max-w-2xl lg:max-w-4xl xl:max-w-5xl mx-auto bg-slate-50 items-center justify-center p-8 relative overflow-hidden font-sans">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-slate-800 animate-spin mx-auto mb-4" />
            <p className="text-slate-900 text-lg font-semibold">Loading data...</p>
            <p className="text-slate-500 text-sm mt-2">Please wait while we connect to the database</p>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  if (loadError) {
    return (
      <ErrorBoundary>
        <div className="flex flex-col min-h-screen w-full max-w-md sm:max-w-2xl lg:max-w-4xl xl:max-w-5xl mx-auto bg-red-600 items-center justify-center p-8 relative overflow-hidden font-sans">
          <div className="text-center">
            <AlertTriangle className="w-16 h-16 text-white mx-auto mb-6" />
            <h1 className="text-white text-2xl font-bold mb-4">Database Connection Error</h1>
            <div className="bg-red-800 rounded-lg p-4 mb-4 text-left">
              <p className="text-red-100 text-sm font-mono break-all">{loadError}</p>
            </div>
            <p className="text-red-100 text-sm">Please check your internet connection and try again.</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-6 bg-white text-red-600 px-6 py-3 rounded-lg font-semibold hover:bg-red-50 transition-colors"
            >
              Retry Connection
            </button>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  if (!currentUser) {
    return (
      <ErrorBoundary>
        <LoginScreen employees={employees} onLogin={handleLogin} />
      </ErrorBoundary>
    );
  }

  if (isDevAdminRoute && !isDevAdminAuthorized) {
    return null;
  }

  if (isDevAdminRoute) {
    return (
      <ErrorBoundary>
        <DevAdminPanel currentUser={currentUser} onBack={() => navigateToPath('/', true)} />
        <Toaster />
        <Analytics />
      </ErrorBoundary>
    );
  }

  const isManager = currentUser.role === 'manager' || currentUser.role === 'super_admin' || currentUser.role === 'owner';
  const isSuperAdmin = currentUser.role === 'super_admin';
  const scopedEmployees = scopeEmployeesForCurrentUser(employees, tasks, currentUser, staffManagerLinks);

  return (
    <ErrorBoundary>
      <div className="min-h-[100dvh] w-full bg-[var(--surface)] overflow-x-hidden">
        <div className="flex min-h-[100dvh] w-full max-w-md flex-col overflow-hidden bg-[var(--surface)] font-sans sm:mx-auto sm:max-w-2xl sm:rounded-[2rem] sm:border sm:border-[var(--border)] sm:my-8 sm:shadow-2xl lg:max-w-4xl xl:max-w-5xl">

      {/* Notification Banner */}
      {notification && (
        <div
          className="fixed left-2 right-2 max-w-md sm:max-w-2xl lg:max-w-4xl xl:max-w-5xl mx-auto z-[100] animate-in slide-in-from-top-4 duration-500"
          style={{ top: 'calc(env(safe-area-inset-top) + 8px)' }}
          onClick={() => handleNotificationBannerClick(notification.taskId)}
        >
          <div className="bg-white text-slate-900 p-4 rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-[var(--border)] flex items-center gap-4">
            <div className="bg-[var(--accent)] p-2 rounded-xl">
              <Bell className="w-5 h-5 animate-bounce text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="section-kicker">{notification.title}</p>
              <p className="text-sm font-bold truncate">{notification.message}</p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setNotification(null);
              }}
              className="p-1 hover:bg-slate-100 rounded-lg text-slate-500 min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        </div>
      )}

      <AppHeader
        unreadNotificationCount={unreadNotificationCount}
        showNotificationsPanel={showNotificationsPanel}
        notificationsLoading={notificationsLoading}
        userNotifications={userNotifications}
        onToggleNotificationsPanel={toggleNotificationsPanel}
        onCloseNotificationsPanel={() => setShowNotificationsPanel(false)}
        onClearAllNotifications={() => void clearAllNotifications()}
        onMarkNotificationAsRead={(notificationId) => void markNotificationAsRead(notificationId)}
        onNotificationClick={handleNotificationItemClick}
        onLogout={() => void handleLogout()}
        formatNotificationTimeAgo={formatNotificationTimeAgo}
      />

      {/* Main Content */}
	      <main className={`flex-1 overflow-y-auto overflow-x-hidden px-4 pt-5 w-full bg-[var(--surface)] transition-[padding-bottom] duration-200 sm:px-6 lg:px-8 ${isMobileKeyboardActive ? 'pb-4' : 'pb-28'}`}>
        {/* Hidden: Business Overview Content */}
        {/* {activeTab === AppTab.DASHBOARD && (
          <StatsScreen
            tasks={tasks}
            currentUser={currentUser}
            employees={employees}
          />
        )} */}

        {activeTab === AppTab.TASKS && (
          <Dashboard
            key={`dashboard:${resolvedActiveCompanyId || 'none'}:${currentUser.id}`}
            tasks={tasks}
            employees={scopedEmployees}
            currentUser={currentUser}
            tasksTabReselectSignal={tasksTabReselectSignal}
            deepLinkTaskTarget={pendingTaskDeepLink}
            onDeepLinkTaskHandled={clearPendingTaskDeepLink}
            onAddTask={addTask}
            onStartTask={startTask}
            onReopenTask={reopenTask}
            onCompleteTask={completeTask}
            onSaveTaskProof={saveTaskProof}
            onCompleteTaskWithoutPhoto={completeTaskWithoutPhoto}
            onReassignTask={reassignTask}
            onDelegateTask={delegateTaskToParticipant}
            onDeleteTask={deleteTask}
            onUpdateTaskRemarks={updateTaskRemarks}
          />
        )}

        {activeTab === AppTab.APPROVALS && (
          <ApprovalsPanel
            key={`approvals:${resolvedActiveCompanyId || 'none'}:${currentUser.id}`}
            currentUser={currentUser}
            deepLinkApprovalTarget={pendingApprovalDeepLink}
            onDeepLinkApprovalHandled={clearPendingApprovalDeepLink}
          />
        )}

        {canAccessCrm && activeTab === AppTab.CRM && resolvedActiveCompanyId && (
          <LeadsScreen
            companyId={resolvedActiveCompanyId}
            currentUser={currentUser}
            employees={scopedEmployees}
          />
        )}

        {isManager && activeTab === AppTab.TEAM && (
          <TeamManager
            key={`team:${resolvedActiveCompanyId || 'none'}:${currentUser.id}`}
            employees={scopedEmployees}
            staffManagerLinks={staffManagerLinks}
            currentUser={currentUser}
            onAddEmployee={addEmployee}
            onRemoveEmployee={removeEmployee}
            onUpdateStaffManagers={updateStaffManagers}
            isSuperAdmin={isSuperAdmin}
            setEmployees={setEmployees}
            onRefreshData={async () => {
              const data = await loadInitialData(false);
              if (data) {
                setEmployees(data.employees);
                setStaffManagerLinks(data.staffManagerLinks || []);
                setTaskParticipants(data.taskParticipants || []);
                setTasks(data.tasks);
              }
            }}
          />
        )}
      </main>

      {/* Simplified Bottom Nav (Dashboard, Tasks, Team) */}
      <nav
        aria-hidden={isMobileKeyboardActive}
        className={`bg-white/96 backdrop-blur border-t border-[var(--border)] fixed bottom-0 left-0 right-0 w-full max-w-md sm:max-w-2xl lg:max-w-4xl xl:max-w-5xl mx-auto z-50 safe-bottom shadow-[0_-2px_10px_rgba(10,10,15,0.06)] rounded-t-[2.5rem] transition-all duration-250 ease-out ${
          isMobileKeyboardActive ? 'pointer-events-none translate-y-[120%] opacity-0' : 'translate-y-0 opacity-100'
        }`}
      >
        <div className="flex justify-around items-center h-20 px-2">

          {/* Hidden: Business Overview Tab */}
          {/* <NavBtn
            active={activeTab === AppTab.DASHBOARD}
            onClick={() => setActiveTab(AppTab.DASHBOARD)}
            icon={<LayoutDashboard className="w-6 h-6" />}
            label="Dashboard"
          /> */}

          <NavBtn
            active={activeTab === AppTab.TASKS}
            onClick={handleTasksTabClick}
            icon={<ClipboardList className="w-6 h-6" />}
            label="Tasks"
          />

          <NavBtn
            active={activeTab === AppTab.APPROVALS}
            onClick={handleApprovalsTabClick}
            icon={<CheckCircle2 className="w-6 h-6" />}
            label="Approvals"
          />

          {canAccessCrm && (
            <NavBtn
              active={activeTab === AppTab.CRM}
              onClick={handleCrmTabClick}
              icon={<BriefcaseBusiness className="w-6 h-6" />}
              label="CRM"
            />
          )}

          {isManager && (
            <NavBtn
              active={activeTab === AppTab.TEAM}
              onClick={handleTeamTabClick}
              icon={<Users className="w-6 h-6" />}
              label="Team"
            />
          )}

        </div>
      </nav>
        </div>
        <Toaster />
        <Analytics />
      </div>
    </ErrorBoundary>
  );
};

const NavBtn = ({ active, onClick, icon, label }: { active: boolean, onClick: () => void, icon: any, label: string }) => (
  <button onClick={onClick} className={`flex-1 flex flex-col items-center gap-1.5 transition-all duration-200 min-w-[70px] ${active ? 'text-[var(--accent)]' : 'text-[var(--ink-3)]'}`}>
    <div className={`p-2.5 rounded-[1.2rem] transition-all duration-300 ${active ? 'bg-[var(--accent-light)] text-[var(--accent)] scale-105 shadow-[0_4px_12px_rgba(79,70,229,0.14)]' : 'text-[var(--ink-3)]'}`}>{icon}</div>
    <span className={`font-ui-mono text-[8px] font-medium uppercase tracking-[0.2em] ${active ? 'opacity-100' : 'opacity-70'}`}>{label}</span>
  </button>
);

export default App;


